/* eslint-disable import/prefer-default-export */
// ll-standards-ignore LL086 -- pre-existing store, already over the guideline before this branch;
// this branch's +58 lines follow the file's own existing action pattern (mutation + wrapper
// function + reload). Splitting this store into services/composables is real, worthwhile work,
// but it's a separate, dedicated effort — not something to fold into sc-46626's scope.
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useMutation, useQuery, useQueryCache } from '@pinia/colada';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getSettlement,
  getSettlementById,
  updateSettlement,
  getDeposits,
  createSettlement as createSettlementApi,
  confirmDeposit as confirmDepositApi,
  getMatterExpenses,
  getMatterBilledExpenses,
  confirmExpenses,
  confirmExpenseInterest,
  getLiensFromTable,
  confirmLiens as confirmLiensApi,
  getAttorneyFee as getAttorneyFeeApi,
  recordAttorneyFee as recordAttorneyFeeApi,
  updateAttorneyFee as updateAttorneyFeeApi,
  confirmAttorneyFee as confirmAttorneyFeeApi,
  generateStatement as generateStatementApi,
  resetSettlement as resetSettlementApi,
  resetSettlementsForClient as resetSettlementsForClientApi,
  resetAndUpdateSettings as resetAndUpdateSettingsApi,
  downloadStatementBlob,
  downloadPreviewStatementBlob,
  downloadTrustStatementBlob as downloadTrustStatementBlobApi,
  getClientPayments,
  confirmClientPayments as confirmClientPaymentsApi,
  getTrustBalance as getTrustBalanceApi,
  finalizeInvoice as finalizeInvoiceApi,
  undoInvoice as undoInvoiceApi,
  payFromTrust as payFromTrustApi,
  undoTrustPayment as undoTrustPaymentApi,
  pushToQbo as pushToQboApi,
  undoQboDisbursement as undoQboDisbursementApi,
  getNarrative as getNarrativeApi,
  saveNarrative as saveNarrativeApi,
  restoreNarrativeFromTemplate as restoreNarrativeFromTemplateApi,
  acquireNarrativeLock as acquireNarrativeLockApi,
  releaseNarrativeLock as releaseNarrativeLockApi,
} from '@/services/settlement/SettlementService';
import {
  getDisbursements as getDisbursementsApi,
  submitDisbursements as submitDisbursementsApi,
  undoDisbursements as undoDisbursementsApi,
  recordManualDisbursement as recordManualDisbursementApi,
  sendClientDisbursement as sendClientDisbursementApi,
  cancelClientDisbursement as cancelClientDisbursementApi,
  getDisbursementEmail as getDisbursementEmailApi,
  resendDisbursementEmail as resendDisbursementEmailApi,
} from '@/services/settlement/DisbursementService';
import {
  getMatterRecipients as getMatterRecipientsApi,
  getCoCounselExpenses as getCoCounselExpensesApi,
} from '@/services/settlement/SettlementRecipientsService';
import { rowKeyForRef, CLIENT_ROW_KEY } from '@/utils/disbursementHelpers';
import type {
  DisbursementEmailComposition,
  DisbursementsState,
  DisbursementRowRef,
  DisbursementRowResult,
  SendClientDisbursementPayload,
} from '@/models/disbursementTypes';
import { daysBetween, computeExpenseInterest } from '@/utils/expenseInterest';
import { yyyymmdddash } from '@/services/utils';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';
import type {
  SettlementResponse,
  SettlementSettings,
  UpdateSettlementRequest,
  ConfirmDepositPayload,
  DepositItem,
  ConfirmExpensesRequest,
  DepositsState,
  ExpensesState,
  LiensState,
  FromTableLiensResponse,
  RecordFeeRequest,
  StatementState,
  ConfirmClientPaymentsPayload,
  DisplayPaymentItem,
  PostSettlementState,
  NarrativeState,
  NarrativeContentInput,
  AttorneyFeeState,
  PaymentsState,
  FeeRecipient,
  ApiFeeRecipientData,
  MatterRecipientData,
  CoCounselExpense,
  ApiCoCounselExpenseData,
} from '@/models/settlementTypes';

/**
 * @pinia/colada is used here as an internal request/cache wrapper only — it is never
 * imported by components. Every read is exposed as a `load*`/`fetch*` store action and
 * every write as its usual action (confirmDeposit, recordFee, etc.); the store's own
 * `ref`/`reactive` state remains the sole public contract MatterSettlement.vue and the
 * step components consume. Most queries below are intentionally driven via `.refetch()`
 * with `staleTime: 0` — `.refetch()` (`queryCache.fetch`) always ignores freshness and
 * hits the network, so `staleTime` only matters for the couple of queries below that are
 * deliberately switched to `.refresh()` (`queryCache.refresh`, which only fetches when
 * stale or errored). Don't add a `staleTime` to a `.refetch()`-driven query — it would be
 * dead configuration.
 *
 * Every `.refetch()`/`.refresh()` call below passes `true` for `throwOnError` — its
 * documented default (`false`) swallows a failed fetch and resolves with the entry's
 * last-known (possibly stale) state instead of rejecting, which would silently hide
 * failures from the surrounding try/catch in every action below. `throwOnError` only
 * governs a real query-function failure, though — it does NOT apply to the supersede
 * case documented next, which never throws at all.
 *
 * Every `useQuery()` below follows the same shape: `enabled: false`, keyed off one (or a
 * few) module-scoped `ref`s that the corresponding `load*`/`fetch*` action sets
 * immediately before calling `.refetch()`/`.refresh()`. Key and query function always
 * read the same ref value *within one call* — but two independent call sites for the
 * same action (e.g. a wizard-shell eager preload racing a step component's own mount)
 * still share the exact same key, since it's derived only from `settlementId`/`matterId`.
 * When that happens, Colada aborts the older call's `AbortController` — but unless the
 * query function actually forwards that `signal` down to the real request (only
 * `disbursementsQuery` does, so only there does the older call's `.refetch()` genuinely
 * reject with a cancellation error), the underlying network call runs to completion on
 * its own and does NOT reject: the older call's own response, whenever it lands, resolves
 * with `entry.state.value` (the cache entry's *current* state) rather than its own
 * response. If nothing has resolved yet that's `{ data: undefined, status: 'pending' }`,
 * so any unguarded `state.data!.someField` on the older call throws, and a generic
 * `catch` turns that into a spurious error toast — not a real failure. Any action reachable
 * from more than one call site needs a monotonic request-token guard (bump a counter,
 * capture it, bail out silently if it no longer matches after the await, in the catch, and
 * in the finally) — see `expenses.requestToken`, `depositsLoadToken`,
 * `disbursementsLoadToken`, `attorneyFeeLoadToken`, and `liensFromTableLoadToken` below.
 * Also never flip one of these queries to `enabled: true`, and never call
 * `invalidateQueries` with `refetchActive` on one, without first moving its params from
 * the shared ref into the key itself (e.g. via `defineQueryOptions` args) — otherwise a
 * background refetch reads whatever the ref *currently* holds and can write one matter's/
 * range's response into another's cache entry.
 */

/** Confirmed settlement-level snapshot → display shape (matterSettlementRecipientId → id). */
function mapApiFeeRecipient(r: ApiFeeRecipientData): FeeRecipient {
  return {
    id: r.matterSettlementRecipientId,
    vendorId: r.vendorId,
    vendorDisplayName: r.vendorDisplayName,
    role: r.role,
    percentage: r.percentage,
    calculatedShareAmount: r.calculatedShareAmount,
    disbursementPushed: r.disbursementPushed,
    disbursementQboId: r.disbursementQboId,
  };
}

/** Matter-level default → display shape, for pre-confirm auto-populate. Never
 *  confirmed yet, so calculatedShareAmount/disbursement fields start empty. */
function mapMatterRecipient(r: MatterRecipientData): FeeRecipient {
  return {
    id: r.matterSettlementRecipientId,
    vendorId: r.vendorId,
    vendorDisplayName: r.vendorDisplayName,
    role: r.role,
    percentage: r.percentage,
    calculatedShareAmount: null,
    disbursementPushed: false,
    disbursementQboId: null,
  };
}

/** Raw API row → display shape (matterSettlementRecipientId → id) — same
 *  mapping shape as mapMatterRecipient/mapApiFeeRecipient above. */
function mapCoCounselExpense(r: ApiCoCounselExpenseData): CoCounselExpense {
  return {
    id: r.matterSettlementRecipientId,
    vendorId: r.vendorId,
    vendorDisplayName: r.vendorDisplayName,
    role: r.role,
    amount: r.amount,
  };
}

const createInitialDeposits = (): DepositsState => ({
  items: [],
  selectedIds: [],
  totalAvailable: 0,
  loading: false,
  error: null,
  filterStartDate: null,
  filterEndDate: null,
});

const createInitialExpenses = (): ExpensesState => ({
  items: [],
  totalExpenses: 0,
  billedExpensesTotal: 0,
  billedItems: [],
  loading: false,
  error: null,
  isConfirmed: false,
  interestConfirmed: false,
  expenseFromDate: null,
  confirmedExpenseFromDate: null,
  confirmedTotalInterest: 0,
  orderby: 'date',
  order: 'asc',
  requestToken: 0,
});

const createInitialLiens = (): LiensState => ({
  items: [],
  totalLiens: 0,
  loading: false,
  error: null,
});

const createInitialAttorneyFee = (): AttorneyFeeState => ({
  calculatedFee: null,
  existingFees: [],
  hasMismatch: false,
  settlementFeeChargeId: null,
  selectedChargeId: null,
  summary: null,
  loading: false,
  recording: false,
  feeRecipients: [],
  noMatterRecipientsConfigured: false,
  recipientsLoading: false,
});

const createInitialStatement = (): StatementState => ({
  generating: false,
  polling: false,
  timedOut: false,
  generatedAt: null,
  blobUrl: null,
  previewGenerating: false,
  previewPolling: false,
  previewTimedOut: false,
  previewBlobUrl: null,
});

const createInitialPayments = (): PaymentsState => ({
  items: [],
  selectedIds: [],
  frozenSelections: [],
  loading: false,
  error: null,
});

const createInitialPostSettlement = (): PostSettlementState => ({
  saving: false,
  error: null,
  trustBalance: null,
  trustBalanceLoading: false,
  trustBalanceError: false,
});

/** Redesigned Step 6 disbursement table (settlementRedesign flipper). */
const createInitialDisbursements = (): DisbursementsState => ({
  data: null,
  loading: false,
  error: null,
  submittingKeys: [],
  rowErrors: {},
  bulkBusy: false,
});

const createInitialNarrative = (): NarrativeState => ({
  enableOpeningParagraph: true,
  openingParagraphHtml: null,
  enableClosingParagraph: true,
  closingParagraphHtml: null,
  jurisdictionNote: null,
  isCustomized: false,
  isStale: false,
  loading: false,
  saving: false,
  editorLock: null,
  updatedAt: null,
});

export const useSettlementStore = defineStore('settlementStore', () => {
  const queryCache = useQueryCache();

  // ── State ──────────────────────────────────────────────
  const loading = ref(true);
  const saving = ref(false);
  const data = ref<SettlementResponse | null>(null);
  const settlementId = ref<string | null>(null);
  const error = ref<unknown>(null);
  const deposits = reactive<DepositsState>(createInitialDeposits());
  const expenses = reactive<ExpensesState>(createInitialExpenses());
  /** Step 2's co-counsel/referring-attorney expense rows — see loadCoCounselExpenses. */
  const coCounselExpenses = ref<CoCounselExpense[]>([]);
  const liens = reactive<LiensState>(createInitialLiens());
  const fromTableLiens = ref<FromTableLiensResponse | null>(null);
  const attorneyFee = reactive<AttorneyFeeState>(createInitialAttorneyFee());
  const statement = reactive<StatementState>(createInitialStatement());
  const payments = reactive<PaymentsState>(createInitialPayments());
  const postSettlement = reactive<PostSettlementState>(createInitialPostSettlement());
  /** Redesigned Step 6 disbursement table (settlementRedesign flipper). */
  const disbursements = reactive<DisbursementsState>(createInitialDisbursements());
  /** Settlement date from the matter (yyyy-MM-dd). Populated by MatterSettlement after load. */
  const settlementDate = ref<string | null>(null);
  /** Company-level expense interest rate (%). Populated by MatterSettlement after load. */
  const companyExpenseInterestRate = ref<number | null>(null);
  const narrative = reactive<NarrativeState>(createInitialNarrative());

  // ── Getters ────────────────────────────────────────────

  /**
   * Returns an array of completed step numbers (1-based).
   * Derives from the API steps array when available, falls back to local heuristics.
   */
  const completedSteps = computed<number[]>(() => {
    if (!data.value) return [];
    // Use steps array from API if present
    if (data.value.steps && data.value.steps.length > 0) {
      return data.value.steps
        .filter((s) => {
          const st = s.status?.toLowerCase();
          return st === 'completed' || st === 'skipped';
        })
        .map((s) => s.stepNumber);
    }
    // Fallback: derive from data fields
    const steps: number[] = [];
    if (data.value.recoveryAmount != null) steps.push(1);
    if (expenses.isConfirmed) steps.push(2);
    // Steps 3-6 will be derived in later stories
    return steps;
  });

  /** First incomplete step (1-based), or 6 if all done. */
  const currentStep = computed<number>(() => {
    for (let i = 1; i <= 6; i += 1) {
      if (!completedSteps.value.includes(i)) return i;
    }
    return 6;
  });

  /** Whether any wizard step has been completed. */
  const hasCompletedSteps = computed<boolean>(() => completedSteps.value.length > 0);

  /** Sum of all selected deposit amounts — the live recovery amount before confirmation. */
  const recoveryAmount = computed<number>(() =>
    deposits.items
      .filter((d) => deposits.selectedIds.includes(d.depositId))
      .reduce((sum, d) => sum + d.amount, 0),
  );

  /** True when at least 1 deposit is selected and recovery > 0 */
  const canConfirmStep1 = computed<boolean>(
    () => deposits.selectedIds.length > 0 && recoveryAmount.value > 0,
  );

  /** Deposits this settlement is allowed to claim — BR-V4-8 locks the rest to a sibling matter. */
  const selectableDeposits = computed<DepositItem[]>(() => deposits.items.filter((d) => !d.isLocked));

  /** True when all selectable deposits are selected */
  const allDepositsSelected = computed<boolean>(
    () =>
      selectableDeposits.value.length > 0 &&
      deposits.selectedIds.length === selectableDeposits.value.length,
  );

  /**
   * Display-ready payment list.  When frozen selections exist (previously
   * confirmed) we show the frozen snapshot so amounts stay stale/stable.
   * Invoice references are enriched from live data by the BE, so frozen
   * rows still get "Payment — Inv #1042" links.
   * Falls back to live items on first visit.
   */
  const displayPayments = computed<DisplayPaymentItem[]>(() => {
    const source =
      payments.frozenSelections.length > 0 ? payments.frozenSelections : payments.items;
    return source.map((p) => ({
      externalPaymentId: p.externalPaymentId,
      paymentDate: p.paymentDate,
      amount: p.amount,
      method: p.method,
      memo: p.memo,
      invoices: p.invoices ?? [],
      isIncluded: payments.selectedIds.includes(p.externalPaymentId),
      isLocked: p.isLocked ?? false,
      lockedByMatterName: p.lockedByMatterName ?? null,
    }));
  });

  /** Sum of selected (included) payment amounts — what the client has already paid.
   *  Uses displayPayments so toggles are always reflected. */
  const clientPaidTotal = computed<number>(() =>
    displayPayments.value.filter((p) => p.isIncluded).reduce((sum, p) => sum + p.amount, 0),
  );

  /** Whether the attorney fee can be confirmed (fee selected; mismatch is allowed since confirm auto-patches). */
  const canConfirmStep3 = computed<boolean>(() => attorneyFee.selectedChargeId != null);

  /** 'match' | 'mismatch' | 'none' depending on fee state */
  const feeMatchStatus = computed<'match' | 'mismatch' | 'none'>(() => {
    if (!attorneyFee.selectedChargeId) return 'none';
    return attorneyFee.hasMismatch ? 'mismatch' : 'match';
  });

  /** Whether the statement can be generated (all prior steps 1–5 done, not yet generated). */
  const canGenerateStatement = computed<boolean>(() => {
    if (!data.value) return false;
    // Already generated — no need to generate again
    if (data.value.blobPath) return false;
    // All steps 1–5 must be completed (or skipped)
    const completed = data.value.steps
      .filter((s) => {
        const st = s.status?.toLowerCase();
        return st === 'completed' || st === 'skipped';
      })
      .map((s) => s.stepNumber);
    // Step 5 (Map Prior Payments) is only required for Client expense arrangement
    const requiredSteps =
      data.value.expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT ? [1, 2, 3, 4, 5] : [1, 2, 3, 4];
    return requiredSteps.every((n) => completed.includes(n));
  });

  /** True when the statement PDF has been generated (blobPath is present). */
  const isGenerated = computed<boolean>(() => !!data.value?.blobPath);

  /** True when the settlement is locked (statement generated — changes require Unlock & Reset). */
  const isLocked = computed<boolean>(() => {
    const st = data.value?.status?.toLowerCase();
    return (
      st === 'statementgenerated' ||
      st === 'statementgenerating' ||
      st === 'invoiced' ||
      st === 'paidlocked' ||
      !!data.value?.blobPath
    );
  });

  /** True when the settlement can be fully reset. Backend-computed (VR-011) and
   *  already accounts for pushed fee-recipient disbursements. */
  const canReset = computed<boolean>(() => data.value?.canReset ?? true);

  /** Reason the reset is blocked (shown as tooltip when canReset is false). */
  const resetBlockedReason = computed<string | null>(() => data.value?.resetBlockedReason ?? null);

  /** True when all settings fields should be read-only (locked or reset blocked). */
  const settingsLocked = computed<boolean>(() => isLocked.value || !canReset.value);

  /** Reason the QBO disbursement push is blocked (shown as tooltip or error when cannot push). */
  const disbursementBlockedReason = computed<string | null>(
    () => data.value?.disbursementBlockedReason ?? null,
  );

  /** Reason client-disbursement Undo is blocked (sc-46091: e.g. QBO Cleared/Reconciled). */
  const undoDisbursementBlockedReason = computed<string | null>(
    () => data.value?.undoDisbursementBlockedReason ?? null,
  );

  /** The authenticated download URL for the generated settlement PDF (null if not yet generated). */
  const statementDownloadUrl = computed<string | null>(() => {
    const ready = !!data.value?.blobPath;
    if (!ready) return null;
    // Only return the local blob URL — the cross-origin API URL would cause
    // the browser to ignore the `download` attribute and open inline instead.
    return statement.blobUrl ?? null;
  });

  /** The local blob URL for the preview PDF (null if not yet generated). */
  const previewDownloadUrl = computed<string | null>(() => statement.previewBlobUrl ?? null);

  /** Settlement settings in a convenient shape. */
  const settings = computed<SettlementSettings | null>(() => {
    if (!data.value) return null;
    return {
      feeMethod: data.value.feeMethod,
      expenseArrangement: data.value.expenseArrangement,
      feePercentage: data.value.feePercentage,
      roundToNearestDollar: data.value.roundToNearestDollar,
    };
  });

  // ── Lien Getters ───────────────────────────────────

  /** Number of lien records (not the monetary total). */
  const lienCount = computed<number>(() => {
    if (fromTableLiens.value) {
      return fromTableLiens.value.lienGroups.reduce((sum, g) => sum + g.liens.length, 0);
    }
    return liens.items.length;
  });

  // ── Expense Getters ────────────────────────────────

  /** True when net-of-expenses and expenses exceed recovery (fee base < 0) */
  const hasNegativeFeeBase = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.feeMethod !== FEE_METHOD.NET) return false;
    const recovery = data.value.recoveryAmount ?? 0;
    return expenses.totalExpenses > recovery;
  });

  /** Total interest accrued on all expenses using company rate and as-of date (V2-2.2). */
  const totalInterest = computed<number>(() => {
    const rate = data.value?.companyExpenseInterestRate;
    const asOf = expenses.expenseFromDate;
    // Interest only applies when the firm advances all expenses (BR-V2-33). Today the backend
    // already nulls companyExpenseInterestRate for non-FirmAdvances arrangements, so the rate
    // guard below covers it — but gate on the arrangement explicitly too so this getter stays
    // in lockstep with SettlementExpenseStep's `showInterest` and confirmStep2 never sends a
    // totalInterestAmount the UI never surfaced if that backend contract ever changes.
    if (data.value?.expenseArrangement !== EXPENSE_ARRANGEMENT.FIRM) return 0;
    if (!rate || !asOf) return 0;
    const interestExpenseId = data.value?.interestExpenseId;
    // Sum in integer cents — each per-expense interest is already floored to whole cents
    // (computeExpenseInterest), so this keeps the total exactly consistent with the per-line-item
    // floor rule and avoids binary float drift from accumulating dollar values.
    const cents = expenses.items.reduce((acc, e) => {
      if (!e.date) return acc;
      if (e.isExpenseInterest || e.id === interestExpenseId) return acc;
      return acc + Math.round(computeExpenseInterest(e.amount, rate, daysBetween(e.date, asOf)) * 100);
    }, 0);
    return cents / 100;
  });

  // ── Post-Settlement Getters (V2-FE-1) ─────────────────

  /** True when the invoice was actually submitted to QuickBooks (Created/Paid) — not merely a linked
   *  draft whose submit failed. Keyed on invoiceStatus so a failed finalize shows the retry button. */
  const invoiceFinalized = computed<boolean>(() => !!data.value?.invoiceStatus);

  /** True when the invoice can be finalized now (server-provided flag). */
  const canFinalizeInvoice = computed<boolean>(() => data.value?.canFinalizeInvoice ?? false);

  /** True when the invoice can be undone now (server-provided flag). */
  const canUndoInvoice = computed<boolean>(() => data.value?.canUndoInvoice ?? false);

  /** Reason the invoice action is blocked — shown inline in the panel. */
  const invoiceBlockedReason = computed<string | null>(() => data.value?.invoiceBlockedReason ?? null);

  /** True when the trust disbursement payment has been applied. */
  const trustPaid = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.trustPaymentApplied !== undefined) {
      return data.value.trustPaymentApplied;
    }
    return !!data.value.trustPaymentId || data.value.canUndoTrustPayment === true;
  });

  /** True when the client net proceeds have been pushed to QBO. */
  const qboPushed = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.clientDisbursementPushed !== undefined) {
      return data.value.clientDisbursementPushed;
    }
    return !!data.value.qboDisbursementId;
  });

  /**
   * Whether client net proceeds can be pushed right now.
   * Prefers backend permission flag; falls back to legacy local rule.
   */
  const canPushClientDisbursement = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.canPushClientDisbursement !== undefined) {
      return data.value.canPushClientDisbursement;
    }
    return trustPaid.value && !qboPushed.value;
  });

  /**
   * Whether the pushed client disbursement can be undone right now.
   * Prefers backend permission flag; falls back to legacy local rule.
   */
  const canUndoClientDisbursement = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.canUndoClientDisbursement !== undefined) {
      return data.value.canUndoClientDisbursement;
    }
    return qboPushed.value;
  });

  /** Invoice total — the amount to be paid from trust. */
  const invoiceAmount = computed<number | null>(() => invoiceBalance.value);

  /** Preferred invoice balance from the settlement payload, falling back to the legacy invoiceAmount field. */
  const invoiceBalance = computed<number | null>(() => {
    const d = data.value as
      | {
          invoiceBalance?: number | null;
          invoiceAmount?: number | null;
        }
      | null;
    return d?.invoiceBalance ?? d?.invoiceAmount ?? null;
  });

  /** Preferred trust balance from the settlement payload, falling back to the separately loaded post-settlement balance. */
  const trustBalance = computed<number | null>(() => {
    const d = data.value as { trustBalance?: number | null } | null;
    return d?.trustBalance ?? postSettlement.trustBalance ?? null;
  });

  /** True when trust payment can be created now (server-provided permission). */
  const canPayFromTrust = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.canPayFromTrust !== undefined) {
      return data.value.canPayFromTrust;
    }
    if (!invoiceFinalized.value || trustPaid.value) return false;
    const balance = trustBalance.value;
    if (balance === null) return false;
    return balance >= (invoiceBalance.value ?? 0);
  });

  /** True when trust payment can be undone now (server-provided permission). */
  const canUndoTrustPayment = computed<boolean>(() => {
    if (!data.value) return false;
    if (data.value.canUndoTrustPayment !== undefined) {
      return data.value.canUndoTrustPayment;
    }
    return trustPaid.value && !qboPushed.value;
  });

  /** Reason trust payment create is blocked — shown inline in panel. */
  const trustPaymentBlockedReason = computed<string | null>(
    () => data.value?.trustPaymentBlockedReason ?? null,
  );

  /** Reason trust payment Undo specifically is blocked (workflow order, or sc-46091 QBO Cleared/Reconciled). */
  const undoTrustPaymentBlockedReason = computed<string | null>(
    () => data.value?.undoTrustPaymentBlockedReason ?? null,
  );

  /** Client net recovery — the amount to push to QBO for client disbursement. */
  const netToClient = computed<number | null>(() => data.value?.computed?.clientNet ?? null);

  /** True when invoice is finalized but trust balance is insufficient (display helper). */
  const trustInsufficient = computed<boolean>(() => {
    if (!invoiceFinalized.value) return false;
    if (trustPaid.value) return false;
    const balance = trustBalance.value;
    if (balance === null) return false;
    return balance < (invoiceBalance.value ?? 0);
  });

  // ── Actions ────────────────────────────────────────────

  const settlementMatterId = ref<string | null>(null);
  const settlementQuery = useQuery({
    key: () => ['settlement', settlementMatterId.value],
    query: () => getSettlement(settlementMatterId.value as string),
    enabled: false,
    staleTime: 0,
  });

  /**
   * Load the settlement for a matter.
   * Calls GET /matters/{matterId}/settlement which returns
   * existing settlement data or default values.
   */
  async function load(matterId: string): Promise<void> {
    loading.value = true;
    error.value = null;
    // Clear the previous matter's expense-interest state so it can't bleed into this
    // matter's Settings panel (e.g. Interest As-Of Date defaulting to a prior matter's
    // value) before loadExpenses() below has a chance to populate this matter's own.
    expenses.expenseFromDate = null;
    expenses.confirmedExpenseFromDate = null;
    expenses.interestConfirmed = false;
    expenses.confirmedTotalInterest = 0;
    // Clear the previous matter's co-counsel expenses so they can't bleed into this
    // matter's Step 2 co-counsel section (loadCoCounselExpenses() is only re-triggered
    // by the component's mount guard when this array is empty).
    coCounselExpenses.value = [];
    // Clear the previous matter's disbursement state — especially the transient
    // per-row submit errors — so they can't bleed into this matter's Step 6 table
    // (load() runs on matter change / initial load, never during a submit refresh).
    disbursements.data = null;
    disbursements.error = null;
    disbursements.rowErrors = {};
    disbursements.submittingKeys = [];
    disbursements.bulkBusy = false;
    settlementMatterId.value = matterId;
    try {
      const state = await settlementQuery.refetch(true);
      const response = state.data!;
      data.value = response;
      settlementId.value = response.settlementId ?? null;
      // Trust the persisted as-of date (it's now saved via the settings PATCH), and
      // default to today only when the backend has none. Establishing it here — not only
      // in loadExpenses, which runs once Step 1 is complete — keeps the Settings-panel
      // picker populated (never blank) from the moment the matter loads.
      expenses.expenseFromDate = response.expenseFromDate ?? yyyymmdddash(new Date());
      // Sync fields now returned directly by the settlement API
      if (response.settlementDate !== undefined) {
        settlementDate.value = response.settlementDate ?? null;
      }
      if (response.companyExpenseInterestRate !== undefined) {
        companyExpenseInterestRate.value = response.companyExpenseInterestRate ?? null;
      }
      // Sync generatedAt so the statement step renders correctly on resume
      if (response.blobPath && !statement.generatedAt) {
        statement.generatedAt = response.completedAt ?? new Date().toISOString();
      }
    } catch (err: any) {
      error.value = err;
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to load settlement data',
      );
    } finally {
      loading.value = false;
    }
  }

  // enabled: false is load-bearing here — see the shared-ref-key note in the file
  // header. Don't flip it without first moving matterId/startDate/endDate from
  // this ref into the key itself.
  const depositsQueryParams = ref<{ matterId: string; startDate?: string; endDate?: string } | null>(
    null,
  );
  const depositsQuery = useQuery({
    key: () => [
      'settlement',
      'deposits',
      depositsQueryParams.value?.matterId,
      depositsQueryParams.value?.startDate ?? null,
      depositsQueryParams.value?.endDate ?? null,
    ],
    query: () => {
      const params = depositsQueryParams.value!;
      return getDeposits(params.matterId, params.startDate, params.endDate);
    },
    enabled: false,
    staleTime: 0,
  });
  /** Bumped on every loadDeposits() call so a superseded call can't apply its
   *  response (or clear `loading`) after a newer call for a different date
   *  range has started. Unlike `loadDisbursements`, the query key here
   *  includes startDate/endDate, so two calls with different dates land in
   *  different Colada cache entries and never trigger its same-key abort —
   *  both requests run to completion independently, so this guard is the
   *  only thing preventing an out-of-order response from winning. */
  let depositsLoadToken = 0;

  /**
   * Load trust deposits for this matter (live from QBO via backend).
   * Auto-selects all deposits unless prior selections are saved.
   *
   * startDate/endDate (yyyy-MM-dd) filter the QBO query. The active filter is
   * stored so the UI can display it and callers (e.g. after recording a
   * deposit) can reload with the same range instead of the default.
   */
  async function loadDeposits(matterId: string, startDate?: string, endDate?: string): Promise<void> {
    const token = ++depositsLoadToken;
    deposits.loading = true;
    deposits.error = null;
    deposits.filterStartDate = startDate ?? null;
    deposits.filterEndDate = endDate ?? null;
    depositsQueryParams.value = { matterId, startDate, endDate };
    try {
      const state = await depositsQuery.refetch(true);
      if (token !== depositsLoadToken) return; // superseded by a newer request
      const response = state.data!;
      deposits.items = response.deposits;
      deposits.totalAvailable = response.totalAvailable;
      // The !isLocked term is the real BR-V4-8 guard: a pre-selected row is what confirmDeposit()
      // sums into the recovery amount.
      const savedIds = data.value?.selectedDepositIds ?? [];
      deposits.selectedIds =
        savedIds.length > 0
          ? savedIds.filter((id) => response.deposits.some((d) => d.depositId === id && !d.isLocked))
          : response.deposits.filter((d) => !d.isLocked).map((d) => d.depositId);
    } catch (err: any) {
      if (token !== depositsLoadToken) return; // superseded — resolve quietly
      deposits.error = err;
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to load trust deposits');
    } finally {
      if (token === depositsLoadToken) {
        deposits.loading = false;
      }
    }
  }

  /** Toggle a single deposit's selection. */
  function toggleDeposit(depositId: string): void {
    const idx = deposits.selectedIds.indexOf(depositId);
    if (idx >= 0) {
      deposits.selectedIds.splice(idx, 1);
      return;
    }

    // Deliberately one-directional: removal above is always allowed, since a locked deposit that
    // somehow got selected would otherwise be stuck in a total the backend rejects.
    if (deposits.items.some((d) => d.depositId === depositId && d.isLocked)) return;

    deposits.selectedIds.push(depositId);
  }

  /** Select all or deselect all selectable deposits. */
  function toggleAllDeposits(): void {
    if (allDepositsSelected.value) {
      deposits.selectedIds = [];
    } else {
      deposits.selectedIds = selectableDeposits.value.map((d) => d.depositId);
    }
  }

  const updateSettingsMutation = useMutation({
    mutation: (vars: { matterId: string; patch: UpdateSettlementRequest }) =>
      updateSettlement(vars.matterId, vars.patch),
  });

  /**
   * Update settings (feeMethod, expenseArrangement, roundToNearestDollar).
   * The server resets locks if settings change materially.
   * Uses PATCH with sparse semantics — null fields are not overwritten.
   */
  async function updateSettings(matterId: string, patch: UpdateSettlementRequest): Promise<void> {
    saving.value = true;
    try {
      const response = await updateSettingsMutation.mutateAsync({ matterId, patch });
      data.value = response;
      if (response.settlementDate !== undefined) {
        settlementDate.value = response.settlementDate ?? null;
      }
      if (response.companyExpenseInterestRate !== undefined) {
        companyExpenseInterestRate.value = response.companyExpenseInterestRate ?? null;
      }
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to save settings');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const resetAndUpdateSettingsMutation = useMutation({
    mutation: (vars: { matterId: string; patch: UpdateSettlementRequest }) =>
      resetAndUpdateSettingsApi(vars.matterId, vars.patch),
  });

  /**
   * Apply settings changes and reset all completed wizard steps.
   * Calls the dedicated POST reset-settings endpoint.
   */
  async function resetAndUpdateSettings(matterId: string, patch: UpdateSettlementRequest): Promise<void> {
    saving.value = true;
    try {
      const response = await resetAndUpdateSettingsMutation.mutateAsync({ matterId, patch });
      data.value = response;
      resetStatementAndExpenses();
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to reset and update settings',
      );
      throw err;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Lock a stepper step by sending lock fields (e.g. lockExpenses, lockClientNet).
   * Same PATCH endpoint (and mutation) as updateSettings — lock-specific fields
   * are just a different shape of the same sparse patch.
   */
  async function lockStep(matterId: string, patch: UpdateSettlementRequest): Promise<void> {
    saving.value = true;
    try {
      const response = await updateSettingsMutation.mutateAsync({ matterId, patch });
      data.value = response;
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to lock step');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  // Colada has no built-in polling primitive (no `refetchInterval`) — the 3s/30s
  // interval-and-timeout loop below stays hand-rolled. generate() and
  // generatePreview() each poll the same GET /settlements/{id} endpoint but through
  // their own query (distinct keys below) rather than a shared one: Colada's
  // fetch() aborts the previous in-flight fetch for a given key before starting a
  // new one, so if both loops shared a key, whichever loop's tick fired second
  // would abort the other's fetch — and the aborted loop reads back
  // entry.state.value (possibly still the previous, unrelated response) instead of
  // its own. Reachable in practice: hitting Generate Statement while the Step 6
  // live preview is still polling.
  const statementPollSettlementId = ref<string | null>(null);
  const statementPollQuery = useQuery({
    key: () => ['settlement', 'by-id', statementPollSettlementId.value],
    query: () => getSettlementById(statementPollSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  const previewPollSettlementId = ref<string | null>(null);
  const previewPollQuery = useQuery({
    key: () => ['settlement', 'by-id', 'preview', previewPollSettlementId.value],
    query: () => getSettlementById(previewPollSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  const generateStatementMutation = useMutation({
    // Only pass `preview` through when true — generateStatementApi(id) and
    // generateStatementApi(id, true) are meaningfully different call signatures
    // downstream (mocked in tests by exact arg count), not just an undefined vs
    // omitted distinction.
    mutation: (vars: { settlementId: string; preview?: boolean }) =>
      vars.preview
        ? generateStatementApi(vars.settlementId, vars.preview)
        : generateStatementApi(vars.settlementId),
  });

  /**
   * Generate the settlement statement.
   * 1. POST /settlements/{id}/statement → triggers async PDF generation on the backend.
   * 2. Poll GET /matters/{matterId}/settlement every 3 s, checking data.blobPath.
   * 3. On non-null blobPath: stop polling.
   * 4. After 30 s with no blobPath: set statement.timedOut = true.
   */
  async function generate(matterId: string): Promise<void> {
    if (!settlementId.value) {
      throw new Error('Settlement must be created before generating a statement.');
    }
    statement.generating = true;
    statement.timedOut = false;
    statement.generatedAt = null;
    try {
      await generateStatementMutation.mutateAsync({ settlementId: settlementId.value });
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to start statement generation',
      );
      statement.generating = false;
      throw err;
    }
    statement.generating = false;
    statement.polling = true;

    const POLL_INTERVAL_MS = 3000;
    const MAX_POLL_MS = 30000;
    const startedAt = Date.now();

    const poll = async (): Promise<void> => {
      if (!statement.polling) return; // was cancelled (e.g. unlock)
      try {
        // Poll the settlement-centric endpoint (spec §12.6: GET /settlements/{id})
        statementPollSettlementId.value = settlementId.value!;
        const state = await statementPollQuery.refetch(true);
        const response = state.data!;
        data.value = response;
        if (response.blobPath) {
          statement.generatedAt = response.completedAt ?? new Date().toISOString();
          statement.polling = false;
          return;
        }
      } catch {
        // swallow individual poll errors — keep trying
      }
      if (Date.now() - startedAt >= MAX_POLL_MS) {
        statement.polling = false;
        statement.timedOut = true;
        return;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, POLL_INTERVAL_MS);
      });
      await poll();
    };

    await poll();
  }

  /** Retry polling after a timeout — resets timedOut and resumes. */
  async function retryPolling(matterId: string): Promise<void> {
    statement.timedOut = false;
    statement.polling = true;
    await generate(matterId);
  }

  /**
   * Download the statement PDF via axios (authenticated) and store a local blob URL.
   * The blob URL can be used for VuePdfEmbed, window.open, and download links.
   * Retries up to 3 times with a 2s delay — mirrors fetchPreviewBlobUrl's handling of
   * blob storage propagation lag, which matters here too: a disbursement submit/undo
   * (e.g. the firm-invoice saga) triggers an async statement regeneration that this
   * re-fetch can otherwise race, permanently stranding the Download button disabled.
   */
  async function fetchStatementBlobUrl(): Promise<string | null> {
    if (!settlementId.value) return null;
    // Reuse existing blob URL if available
    if (statement.blobUrl) return statement.blobUrl;

    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
      try {
        const blob = await downloadStatementBlob(settlementId.value);
        const url = URL.createObjectURL(blob);
        statement.blobUrl = url;
        return url;
      } catch (err: any) {
        if (attempt < MAX_RETRIES) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, RETRY_DELAY_MS);
          });
        } else {
          useSnackbarHandler().handleError(
            err?.response?.data?.message || 'Unable to download statement PDF',
          );
          return null;
        }
      }
    }
    return null;
  }

  /**
   * Download the trust statement PDF via axios (authenticated).
   * Returns a Blob so callers can decide whether to preview or trigger download.
   */
  async function downloadTrustStatementBlob(): Promise<Blob | null> {
    if (!settlementId.value) return null;
    try {
      const blob = await downloadTrustStatementBlobApi(settlementId.value);
      return blob;
    } catch (err: any) {
      // When responseType is 'blob', axios parses error bodies as Blob too.
      // We need to read the blob text and parse JSON to get the API message.
      let apiMessage: string | null = null;
      const rawData = err?.response?.data;
      if (rawData instanceof Blob) {
        try {
          const text = await rawData.text();
          const parsed = JSON.parse(text);
          apiMessage = parsed?.message ?? parsed?.detail ?? null;
        } catch {
          // not JSON — ignore
        }
      } else {
        apiMessage = rawData?.message ?? null;
      }
      useSnackbarHandler().handleError(apiMessage || 'Unable to download trust statement PDF');
      return null;
    }
  }

  /** Reset statement generation state, expenses confirmation, and liens (shared by unlock & resetAndUpdateSettings). */
  function resetStatementAndExpenses(): void {
    statement.generatedAt = null;
    statement.generating = false;
    statement.timedOut = false;
    revokeStatementBlobUrl();
    revokePreviewBlobUrl();
    expenses.isConfirmed = false;
    expenses.interestConfirmed = false;
    expenses.expenseFromDate = null;
    expenses.confirmedExpenseFromDate = null;
    expenses.confirmedTotalInterest = 0;
    liens.items = [];
    liens.totalLiens = 0;
    liens.error = null;
    fromTableLiens.value = null;
  }

  /** Revoke the blob URL to free memory. */
  function revokeStatementBlobUrl(): void {
    if (statement.blobUrl) {
      URL.revokeObjectURL(statement.blobUrl);
      statement.blobUrl = null;
    }
  }

  const unlockMutation = useMutation({
    mutation: (vars: { settlementId: string }) => resetSettlementApi(vars.settlementId),
  });

  /**
   * Unlock & Reset a settlement by id — usable for one this store has not
   * loaded (the accounting-mode dialog resets from the Billing tab).
   *
   * Polling is cancelled unconditionally: a statement poll started by the
   * Settlement tab reads `statement.polling` on each tick, and leaving it set
   * would keep it running against steps this call just rolled back.
   */
  async function resetSettlementById(targetSettlementId: string): Promise<void> {
    saving.value = true;
    // Stop any in-progress polling
    statement.polling = false;
    try {
      const response = await unlockMutation.mutateAsync({ settlementId: targetSettlementId });
      if (settlementId.value === targetSettlementId) {
        data.value = response;
        resetStatementAndExpenses();
      }
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to reset settlement');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const clientResetMutation = useMutation({
    mutation: (vars: { clientId: string }) => resetSettlementsForClientApi(vars.clientId),
  });

  let clientResetToken = 0;

  /**
   * Reset every settlement blocking an accounting-mode change for a client, in one
   * backend transaction — `billClient` is client-level, so a partial reset would
   * leave siblings holding dollar snapshots derived under the old mode.
   *
   * Polling is cancelled unconditionally, same reason as resetSettlementById. The
   * refresh is guarded the same way too, and usually skipped: this fires from the
   * Billing tab, where no settlement is loaded. Unlike the single reset there is no
   * settlement payload to assign — the endpoint returns ids only, so the loaded one
   * is re-fetched rather than making every bulk reset pay to rebuild N forecasts.
   *
   * The re-fetch shares `settlementQuery`'s key with load(), so it carries the token
   * guard trap 4 in state-and-data.md describes: a concurrent load() makes Colada
   * abort the loser, which then resolves with the entry's current state rather than
   * rejecting. Hence the token check and the `?? null` — never `state.data!`.
   */
  async function resetSettlementsForClient(clientId: string): Promise<string[]> {
    const token = ++clientResetToken;
    saving.value = true;
    statement.polling = false;
    try {
      const resetIds = await clientResetMutation.mutateAsync({ clientId });
      // Case-insensitive: both sides are GUIDs from the same serialiser today, but a
      // casing drift would silently leave a reset wizard showing confirmed steps.
      const loadedId = settlementId.value?.toLowerCase();
      if (loadedId && resetIds.some((id) => id.toLowerCase() === loadedId)) {
        const state = await settlementQuery.refetch(true);
        if (token !== clientResetToken) return resetIds;
        data.value = state.data ?? null;
        resetStatementAndExpenses();
      }
      return resetIds;
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to reset settlements',
      );
      throw err;
    } finally {
      saving.value = false;
    }
  }

  /** Unlock the settlement to allow re-editing (Unlock & Reset). */
  async function unlock(matterId: string): Promise<void> {
    if (!settlementId.value) {
      throw new Error('No settlement to unlock.');
    }
    return resetSettlementById(settlementId.value);
  }

  /**
   * Generate a preview PDF for Step 6 inline display.
   * 1. POST /settlements/{id}/statement/generate?preview=true → triggers async preview PDF.
   * 2. Poll GET /settlements/{id} every 3 s, checking data.previewBlobPath.
   * 3. On non-null previewBlobPath: download blob and store previewBlobUrl.
   * 4. After 30 s with no previewBlobPath: set statement.previewTimedOut = true.
   */
  async function generatePreview(): Promise<void> {
    if (!settlementId.value) return;
    // Already have a preview — skip
    if (statement.previewBlobUrl) return;
    // Already generating — skip
    if (statement.previewGenerating || statement.previewPolling) return;

    statement.previewGenerating = true;
    statement.previewTimedOut = false;
    try {
      await generateStatementMutation.mutateAsync({ settlementId: settlementId.value, preview: true });
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to generate statement preview',
      );
      statement.previewGenerating = false;
      return;
    }
    statement.previewGenerating = false;
    statement.previewPolling = true;

    // Clear stale previewBlobPath so polling waits for the NEW preview.
    // The backend does NOT clear PreviewStatementBlobPath on generate (unlike
    // StatementBlobPath for final PDF), so without this the first poll would
    // immediately find the old value and try to download a blob that may not
    // exist yet.
    if (data.value) {
      data.value.previewBlobPath = null;
    }

    const POLL_INTERVAL_MS = 3000;
    const MAX_POLL_MS = 30000;
    const startedAt = Date.now();

    const poll = async (): Promise<void> => {
      if (!statement.previewPolling) return;
      try {
        previewPollSettlementId.value = settlementId.value!;
        const state = await previewPollQuery.refetch(true);
        const response = state.data!;
        data.value = response;
        if (response.previewBlobPath) {
          statement.previewPolling = false;
          await fetchPreviewBlobUrl();
          return;
        }
      } catch {
        // swallow individual poll errors — keep trying
      }
      if (Date.now() - startedAt >= MAX_POLL_MS) {
        statement.previewPolling = false;
        statement.previewTimedOut = true;
        return;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, POLL_INTERVAL_MS);
      });
      await poll();
    };

    await poll();
  }

  /**
   * Download the preview PDF via axios (authenticated) and store a local blob URL.
   * Retries up to 3 times with a 2s delay to handle blob storage propagation lag.
   */
  async function fetchPreviewBlobUrl(): Promise<string | null> {
    if (!settlementId.value) return null;
    if (statement.previewBlobUrl) return statement.previewBlobUrl;

    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
      try {
        const blob = await downloadPreviewStatementBlob(settlementId.value);
        const url = URL.createObjectURL(blob);
        statement.previewBlobUrl = url;
        return url;
      } catch (err: any) {
        if (attempt < MAX_RETRIES) {
          // Wait before retrying — blob may not be fully propagated yet
          await new Promise<void>((resolve) => {
            setTimeout(resolve, RETRY_DELAY_MS);
          });
        } else {
          useSnackbarHandler().handleError(
            err?.response?.data?.message || 'Unable to download statement preview',
          );
          return null;
        }
      }
    }
    return null;
  }

  /** Revoke the preview blob URL to free memory. */
  function revokePreviewBlobUrl(): void {
    if (statement.previewBlobUrl) {
      URL.revokeObjectURL(statement.previewBlobUrl);
      statement.previewBlobUrl = null;
    }
  }

  /** Retry preview generation after a timeout. */
  async function retryPreview(): Promise<void> {
    statement.previewTimedOut = false;
    statement.previewBlobUrl = null;
    await generatePreview();
  }

  const createSettlementMutation = useMutation({
    mutation: (vars: { matterId: string; settings: SettlementSettings }) =>
      createSettlementApi(vars.matterId, vars.settings),
  });

  /**
   * Create a settlement row on first commit — either the first Settings-panel save
   * (early-save) or Step 1 confirm, whichever happens first. NOT on tab mount.
   * POST is idempotent — returns existing if already created, so a later call from
   * the other trigger just re-confirms the same settings.
   * Sends the current settings (feeMethod, expenseArrangement, feePercentage,
   * roundToNearestDollar, expenseFromDate, settlementDate) as the POST body so the
   * backend can persist them atomically.
   */
  async function createSettlement(matterId: string, settlementSettings: SettlementSettings): Promise<string> {
    saving.value = true;
    try {
      const response = await createSettlementMutation.mutateAsync({ matterId, settings: settlementSettings });
      data.value = response;
      settlementId.value = response.settlementId;
      return response.settlementId ?? '';
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to create settlement');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const confirmDepositMutation = useMutation({
    mutation: (vars: { settlementId: string; payload: ConfirmDepositPayload }) =>
      confirmDepositApi(vars.settlementId, vars.payload),
  });

  /**
   * Confirm Step 1: freeze selected deposits and run the calculation engine.
   * Calls POST /settlements/{settlementId}/deposit/confirm.
   * The backend replaces any prior deposit selections, recalculates, and
   * marks step 1 as completed.
   */
  async function confirmDeposit(): Promise<void> {
    if (!settlementId.value) {
      throw new Error('Settlement must be created before confirming deposits.');
    }
    const payload: ConfirmDepositPayload = {
      selectedDeposits: deposits.items
        .filter((d) => deposits.selectedIds.includes(d.depositId))
        .map((d) => ({
          externalDepositId: d.depositId,
          amount: d.amount,
          depositDate: d.receivedDate || null,
          description: d.description || '',
          sourceType: 'Deposit',
        })),
      depositsFromDate: deposits.filterStartDate ?? null,
    };
    saving.value = true;
    try {
      const response = await confirmDepositMutation.mutateAsync({ settlementId: settlementId.value, payload });
      data.value = response;
    } catch (err: any) {
      // A 409 means a sibling matter claimed one of these since the list loaded, so reload to render
      // the row locked — otherwise a blind retry hits the same conflict.
      if (err?.response?.status === 409 && depositsQueryParams.value) {
        const { matterId, startDate, endDate } = depositsQueryParams.value;
        const priorSelection = [...deposits.selectedIds];
        await loadDeposits(matterId, startDate, endDate);
        // Step 1 is unconfirmed, so loadDeposits falls to select-all and would silently re-check
        // whatever the user had unchecked — and their selection is what the retry then freezes.
        deposits.selectedIds = priorSelection.filter((id) =>
          deposits.items.some((d) => d.depositId === id && !d.isLocked),
        );
      }
      // loadDeposits never rejects and reports through this same snackbar, so the conflict is
      // raised last to stay the visible message — it is the one that says what to do next.
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to confirm deposits');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const attorneyFeeSettlementId = ref<string | null>(null);
  const attorneyFeeQuery = useQuery({
    key: () => ['settlement', 'attorney-fee', attorneyFeeSettlementId.value],
    query: () => getAttorneyFeeApi(attorneyFeeSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  // MatterSettlement.vue fires this fire-and-forget on mount when resuming a wizard
  // past Step 2, and again after confirmStep2() — either can still be in flight when
  // a settings save's refreshAttorneyFeeIfAvailable() awaits a third call. See the
  // file header for why that needs this token guard.
  let attorneyFeeLoadToken = 0;

  /**
   * Load attorney fee data for Step 3.
   * Calls GET /settlements/{id}/attorney-fee.
   */
  async function loadAttorneyFee(): Promise<void> {
    if (!settlementId.value) return;
    const token = ++attorneyFeeLoadToken;
    attorneyFee.loading = true;
    attorneyFeeSettlementId.value = settlementId.value;
    try {
      const state = await attorneyFeeQuery.refetch(true);
      if (token !== attorneyFeeLoadToken) return; // superseded by a newer request
      const response = state.data!;
      const currentSelection = attorneyFee.selectedChargeId;
      const preservedSelection =
        currentSelection && response.existingFees.some((f) => f.chargeId === currentSelection)
          ? currentSelection
          : response.settlementFeeChargeId;

      attorneyFee.calculatedFee = response.calculatedAttorneyFee;
      attorneyFee.existingFees = response.existingFees;
      attorneyFee.settlementFeeChargeId = response.settlementFeeChargeId;
      attorneyFee.selectedChargeId = preservedSelection;
      const selectedFee = preservedSelection
        ? response.existingFees.find((f) => f.chargeId === preservedSelection)
        : null;
      attorneyFee.hasMismatch = selectedFee ? selectedFee.amount !== response.calculatedAttorneyFee : false;
      attorneyFee.summary = response.summary ?? null;
      attorneyFee.feeRecipients = (response.feeRecipients ?? []).map(mapApiFeeRecipient);
    } catch (err: any) {
      if (token !== attorneyFeeLoadToken) return; // superseded — resolve quietly
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to load attorney fee data',
      );
    } finally {
      if (token === attorneyFeeLoadToken) {
        attorneyFee.loading = false;
      }
    }
  }

  const matterFeeRecipientsMatterId = ref<string | null>(null);
  const matterFeeRecipientsQuery = useQuery({
    key: () => ['settlement', 'matter-fee-recipients', matterFeeRecipientsMatterId.value],
    query: () => getMatterRecipientsApi(matterFeeRecipientsMatterId.value as string),
    enabled: false,
    // Matter-level defaults never change within a session — real caching (not just
    // the guard below) so repeated calls before the guard's condition trips don't
    // re-hit the network. staleTime: Infinity means this entry never becomes
    // eligible for Colada's own gc, and — since this store's useQuery()
    // subscription lives as long as the store itself (never torn down; it
    // outlives every Vue island in the Angular bridge) — nothing else would ever
    // evict it either. loadMatterFeeRecipients() below caps growth to one entry
    // at a time by evicting the previous matter's slot on every matter switch.
    staleTime: Infinity,
  });

  /**
   * Auto-populate Step 3's co-counsel fee recipients from the matter's configured
   * defaults. Only meaningful before Step 3 has been confirmed — loadAttorneyFee
   * already fills feeRecipients from the confirmed settlement snapshot once one
   * exists, so this is a no-op guard against overwriting real confirmed data.
   * Calls GET /matters/{matterId}/settlement/recipients.
   */
  async function loadMatterFeeRecipients(): Promise<void> {
    const matterId = data.value?.matterId;
    if (!matterId || attorneyFee.feeRecipients.length > 0) return;
    attorneyFee.recipientsLoading = true;
    const previousMatterId = matterFeeRecipientsMatterId.value;
    if (previousMatterId && previousMatterId !== matterId) {
      const previousEntry = queryCache.get(['settlement', 'matter-fee-recipients', previousMatterId]);
      if (previousEntry) queryCache.remove(previousEntry);
    }
    matterFeeRecipientsMatterId.value = matterId;
    try {
      const state = await matterFeeRecipientsQuery.refresh(true);
      const response = state.data!;
      if (response.recipients.length === 0) {
        attorneyFee.noMatterRecipientsConfigured = true;
        return;
      }
      attorneyFee.feeRecipients = response.recipients.map(mapMatterRecipient);
      attorneyFee.noMatterRecipientsConfigured = false;
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to load fee recipients',
      );
    } finally {
      attorneyFee.recipientsLoading = false;
    }
  }

  /** Keeps the store in sync with in-progress percentage edits from Step 3. */
  function setFeeRecipients(recipients: FeeRecipient[]): void {
    attorneyFee.feeRecipients = recipients;
  }

  /**
   * Select an existing fee from the list.
   * Updates selectedChargeId and recalculates mismatch status.
   */
  function selectFee(chargeId: string): void {
    attorneyFee.selectedChargeId = chargeId;
    // Recalculate mismatch based on selected fee vs calculated amount
    const selectedFee = attorneyFee.existingFees.find((f) => f.chargeId === chargeId);
    const calculated = attorneyFee.calculatedFee ?? 0;
    attorneyFee.hasMismatch = selectedFee ? selectedFee.amount !== calculated : false;
  }

  const recordFeeMutation = useMutation({
    mutation: (vars: { settlementId: string; payload: RecordFeeRequest }) =>
      recordAttorneyFeeApi(vars.settlementId, vars.payload),
  });

  /**
   * Record a new attorney fee charge.
   * Calls POST /settlements/{id}/attorney-fee/record.
   */
  async function recordFee(payload: RecordFeeRequest): Promise<void> {
    if (!settlementId.value) {
      throw new Error('Settlement must be created before recording a fee.');
    }
    attorneyFee.recording = true;
    try {
      const entry = await recordFeeMutation.mutateAsync({ settlementId: settlementId.value, payload });
      attorneyFee.existingFees.push(entry);
      attorneyFee.settlementFeeChargeId = entry.chargeId;
      // Auto-select the newly created fee
      attorneyFee.selectedChargeId = entry.chargeId;
      attorneyFee.hasMismatch = false;
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to record attorney fee');
      throw err;
    } finally {
      attorneyFee.recording = false;
    }
  }

  const updateFeeMutation = useMutation({
    mutation: (vars: { settlementId: string; chargeId: string; payload: RecordFeeRequest }) =>
      updateAttorneyFeeApi(vars.settlementId, vars.chargeId, vars.payload),
  });

  /**
   * Update an existing fee charge (auto-PATCH on confirm when mismatch exists).
   * Calls PATCH /settlements/{id}/attorney-fee/{chargeId}.
   */
  async function updateFee(chargeId: string, payload: RecordFeeRequest): Promise<void> {
    if (!settlementId.value) return;
    attorneyFee.recording = true;
    try {
      const updated = await updateFeeMutation.mutateAsync({
        settlementId: settlementId.value,
        chargeId,
        payload,
      });
      const idx = attorneyFee.existingFees.findIndex((f) => f.chargeId === chargeId);
      if (idx >= 0) {
        attorneyFee.existingFees[idx] = updated;
      }
      attorneyFee.hasMismatch = false;
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to update attorney fee');
      throw err;
    } finally {
      attorneyFee.recording = false;
    }
  }

  const confirmAttorneyFeeMutation = useMutation({
    mutation: (vars: { settlementId: string; payload: { feeChargeId: string } }) =>
      confirmAttorneyFeeApi(vars.settlementId, vars.payload),
  });

  /**
   * Confirm Step 3: link fee charge to settlement and advance.
   * If a mismatch exists, auto-PATCHes the fee first.
   * Calls POST /settlements/{id}/attorney-fee/confirm.
   */
  async function confirmAttorneyFee(): Promise<void> {
    if (!settlementId.value) {
      throw new Error('Settlement must be created before confirming the attorney fee.');
    }
    const feeChargeId = attorneyFee.selectedChargeId;
    if (!feeChargeId) {
      throw new Error('No fee selected. Select or create a fee first.');
    }
    saving.value = true;
    try {
      // Auto-PATCH if mismatch before confirming
      if (attorneyFee.hasMismatch && attorneyFee.calculatedFee != null) {
        await updateFee(feeChargeId, { amount: attorneyFee.calculatedFee });
      }
      const response = await confirmAttorneyFeeMutation.mutateAsync({
        settlementId: settlementId.value,
        payload: { feeChargeId },
      });
      data.value = response;
      // The confirm response is settlement-shaped, not attorney-fee-shaped — it can't be
      // locally patched into attorneyFee the way recordFee/updateFee do. Re-fetch (via
      // .refetch(), which always hits the network regardless of staleness) so the
      // Attorney Fee step shows the confirmed state when the user returns to Step 3.
      await loadAttorneyFee();
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to confirm attorney fee');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const confirmAttorneyFeeRedesignMutation = useMutation({
    mutation: (vars: {
      settlementId: string;
      payload: {
        feeChargeId?: string;
        feeDescription?: string;
        feeDate?: string;
        feeRecipients?: { recipientId: string; percentage: number }[];
      };
    }) => confirmAttorneyFeeApi(vars.settlementId, vars.payload),
  });

  /**
   * Confirm Step 3 (settlementRedesign flag): single atomic call, no
   * separate record/PATCH round trip. feeChargeId omitted creates a new
   * charge; provided, it updates/links that charge. The backend always uses
   * its own AttorneyFeeSnapshot for the amount — never a client-supplied one,
   * so there is no mismatch to auto-correct here (unlike legacy confirmAttorneyFee).
   * Calls POST /settlements/{id}/attorney-fee/confirm.
   */
  async function confirmAttorneyFeeRedesign(payload: {
    feeChargeId?: string;
    feeDescription?: string;
    feeDate?: string;
    /** Full display-shaped recipients — narrowed to {recipientId, percentage}
     *  here (vendorId/role are display-only and never sent; the backend
     *  resolves them from recipientId). Omit entirely when there's nothing to
     *  submit, e.g. flag off or no recipients configured on this matter. */
    feeRecipients?: FeeRecipient[];
  }): Promise<void> {
    if (!settlementId.value) {
      throw new Error('Settlement must be created before confirming the attorney fee.');
    }
    saving.value = true;
    try {
      const response = await confirmAttorneyFeeRedesignMutation.mutateAsync({
        settlementId: settlementId.value,
        payload: {
          feeChargeId: payload.feeChargeId,
          feeDescription: payload.feeDescription,
          feeDate: payload.feeDate,
          feeRecipients: payload.feeRecipients?.length
            ? payload.feeRecipients.map((r) => ({
                recipientId: r.id,
                percentage: r.percentage,
              }))
            : undefined,
        },
      });
      data.value = response;
      // See confirmAttorneyFee — the confirm response can't be locally patched into
      // attorneyFee, so re-fetch to pick up the (possibly server-created) fee charge.
      await loadAttorneyFee();
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to confirm attorney fee');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  // ── Step 5: Client Payments ─────────────────────────

  const paymentsSettlementId = ref<string | null>(null);
  const paymentsQuery = useQuery({
    key: () => ['settlement', 'client-payments', paymentsSettlementId.value],
    query: () => getClientPayments(paymentsSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  /**
   * Load client payments via GET /settlements/{id}/client-payments.
   * Selection logic (first visit vs return visit) is computed server-side.
   */
  async function loadPayments(): Promise<void> {
    if (!settlementId.value) return;
    payments.loading = true;
    payments.error = null;
    paymentsSettlementId.value = settlementId.value;
    try {
      const state = await paymentsQuery.refetch(true);
      const response = state.data!;
      payments.items = response.payments;
      payments.frozenSelections = response.frozenSelections;
      payments.selectedIds = response.selectedPaymentIds;
    } catch (err: any) {
      payments.error = err;
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to load payments');
    } finally {
      payments.loading = false;
    }
  }

  /** Toggle a single payment's selection (include/exclude). */
  function togglePayment(paymentId: string): void {
    const idx = payments.selectedIds.indexOf(paymentId);
    if (idx >= 0) {
      payments.selectedIds.splice(idx, 1);
      return;
    }
    // Checked against displayPayments, not payments.items: a return visit renders frozenSelections,
    // and that is also what the confirm payload is built from.
    if (displayPayments.value.some((p) => p.externalPaymentId === paymentId && p.isLocked)) {
      return;
    }
    payments.selectedIds.push(paymentId);
  }

  const confirmClientPaymentsMutation = useMutation({
    mutation: (vars: { settlementId: string; payload: ConfirmClientPaymentsPayload }) =>
      confirmClientPaymentsApi(vars.settlementId, vars.payload),
  });

  /**
   * Confirm Step 5: freeze selected client payments and run recalculation.
   * Calls POST /settlements/{settlementId}/client-payments/confirm.
   */
  async function confirmClientPayments(): Promise<void> {
    if (!settlementId.value) {
      throw new Error('Settlement must be created before confirming payments.');
    }
    const payload: ConfirmClientPaymentsPayload = {
      allPayments: displayPayments.value.map((p) => ({
        externalPaymentId: p.externalPaymentId,
        amount: p.amount,
        paymentDate: p.paymentDate || null,
        method: p.method || null,
        memo: p.memo || null,
        sourceType: 'Payment',
        isIncluded: p.isIncluded,
      })),
    };
    saving.value = true;
    try {
      const response = await confirmClientPaymentsMutation.mutateAsync({
        settlementId: settlementId.value,
        payload,
      });
      data.value = response;
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to confirm payment mapping',
      );
      if (err?.response?.status === 409) {
        try {
          await loadPayments();
        } catch {
          // Leave the original conflict as the reported failure.
        }
      }
      throw err;
    } finally {
      saving.value = false;
    }
  }

  // ── Post-Settlement Actions (V2-FE-1) ─────────────────

  const trustBalanceMatterId = ref<string | null>(null);
  const trustBalanceQuery = useQuery({
    key: () => ['settlement', 'trust-balance', trustBalanceMatterId.value],
    query: () => getTrustBalanceApi(trustBalanceMatterId.value as string),
    enabled: false,
    // Short cache window: cheap to tolerate a few seconds of staleness, but
    // finalizeInvoice/undoInvoice/payFromTrust/undoTrustPayment/pushToQbo all
    // invalidate this key explicitly (see below) so it never shows a stale
    // balance right after one of those mutations succeeds.
    staleTime: 30_000,
  });

  /** Load trust balance in parallel with settlement data. Fails gracefully — shows — on error. */
  async function loadTrustBalance(matterId: string): Promise<void> {
    postSettlement.trustBalanceLoading = true;
    postSettlement.trustBalanceError = false;
    trustBalanceMatterId.value = matterId;
    try {
      const state = await trustBalanceQuery.refresh(true);
      postSettlement.trustBalance = state.data?.balance ?? null;
    } catch {
      postSettlement.trustBalanceError = true;
      postSettlement.trustBalance = null;
    } finally {
      postSettlement.trustBalanceLoading = false;
    }
  }

  /** Invalidate and re-load the trust balance after a mutation that can change it
   *  (finalize/undo invoice, pay/undo trust, push QBO) — without this the post-
   *  settlement panel can show a stale balance until the tab is re-entered. */
  async function refreshTrustBalanceAfterMutation(): Promise<void> {
    const matterId = data.value?.matterId;
    if (!matterId) return;
    queryCache.invalidateQueries({ key: ['settlement', 'trust-balance', matterId] });
    await loadTrustBalance(matterId);
  }

  const finalizeInvoiceMutation = useMutation({
    mutation: (vars: { settlementId: string }) => finalizeInvoiceApi(vars.settlementId),
  });

  /** Finalize the settlement invoice (Column 1). Updates settlement data on success. */
  async function finalizeInvoice(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    postSettlement.saving = true;
    postSettlement.error = null;
    try {
      const response = await finalizeInvoiceMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response;
      // Surface non-fatal warnings (e.g. stale statement) that would otherwise be invisible.
      response.warnings?.forEach((w) => useSnackbarHandler().handleWarning(w));
      // Re-fetch the regenerated statement blob (revoke alone would hide the Download button).
      revokeStatementBlobUrl();
      await fetchStatementBlobUrl();
      await refreshTrustBalanceAfterMutation();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to finalize invoice';
      postSettlement.error = msg;
      useSnackbarHandler().handleError(msg);
      // Fail-loud finalize may have created a draft before the submit failed. Refresh so the panel
      // shows the retry state (Finalize enabled) instead of stale pre-finalize data until a reload.
      const matterId = data.value?.matterId;
      if (matterId) {
        try {
          data.value = await getSettlement(matterId);
        } catch {
          /* keep prior data if the refresh itself fails */
        }
      }
      throw err;
    } finally {
      postSettlement.saving = false;
    }
  }

  const undoInvoiceMutation = useMutation({
    mutation: (vars: { settlementId: string }) => undoInvoiceApi(vars.settlementId),
  });

  /** Undo the finalized invoice (Column 1 undo). Only valid when trust has not been paid. */
  async function undoInvoice(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    postSettlement.saving = true;
    postSettlement.error = null;
    try {
      const response = await undoInvoiceMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response;
      // Surface non-fatal warnings (e.g. stale statement) that would otherwise be invisible.
      response.warnings?.forEach((w) => useSnackbarHandler().handleWarning(w));
      // Re-fetch the regenerated statement blob (revoke alone would hide the Download button).
      revokeStatementBlobUrl();
      await fetchStatementBlobUrl();
      await refreshTrustBalanceAfterMutation();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to undo invoice';
      postSettlement.error = msg;
      useSnackbarHandler().handleError(msg);
      throw err;
    } finally {
      postSettlement.saving = false;
    }
  }

  const payFromTrustMutation = useMutation({
    mutation: (vars: { settlementId: string }) => payFromTrustApi(vars.settlementId),
  });

  /** Disburse invoice amount from trust (Column 2). Only valid when invoice is finalized. */
  async function payFromTrust(): Promise<string | null> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    postSettlement.saving = true;
    postSettlement.error = null;
    try {
      const response = await payFromTrustMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response.settlement;
      await refreshTrustBalanceAfterMutation();
      return response.message;
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to pay from trust';
      postSettlement.error = msg;
      useSnackbarHandler().handleError(msg);
      throw err;
    } finally {
      postSettlement.saving = false;
    }
  }

  const undoTrustPaymentMutation = useMutation({
    mutation: (vars: { settlementId: string }) => undoTrustPaymentApi(vars.settlementId),
  });

  /** Undo the trust disbursement (Column 2 undo). Only valid when QBO has not been pushed. */
  async function undoTrustPayment(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    postSettlement.saving = true;
    postSettlement.error = null;
    try {
      const response = await undoTrustPaymentMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response;
      await refreshTrustBalanceAfterMutation();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to undo trust payment';
      postSettlement.error = msg;
      useSnackbarHandler().handleError(msg);
      throw err;
    } finally {
      postSettlement.saving = false;
    }
  }

  const pushToQboMutation = useMutation({
    mutation: (vars: { settlementId: string }) => pushToQboApi(vars.settlementId),
  });

  /** Push client net proceeds to QBO (Column 3). Only valid when trust payment is done. */
  async function pushToQbo(): Promise<string | null> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    postSettlement.saving = true;
    postSettlement.error = null;
    try {
      const response = await pushToQboMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response.settlement;
      await refreshTrustBalanceAfterMutation();
      return response.message;
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to push to QBO';
      postSettlement.error = msg;
      useSnackbarHandler().handleError(msg);
      throw err;
    } finally {
      postSettlement.saving = false;
    }
  }

  const undoQboDisbursementMutation = useMutation({
    mutation: (vars: { settlementId: string }) => undoQboDisbursementApi(vars.settlementId),
  });

  /** Undo the QBO disbursement entry (Column 3 undo). Strict reverse — must be done first. */
  async function undoQboDisbursement(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    postSettlement.saving = true;
    postSettlement.error = null;
    try {
      const response = await undoQboDisbursementMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response;
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to undo QuickBooks disbursement';
      postSettlement.error = msg;
      useSnackbarHandler().handleError(msg);
      throw err;
    } finally {
      postSettlement.saving = false;
    }
  }

  // ── Step 6: Disbursement table (settlementRedesign) ────────

  // enabled: false is load-bearing here — see the shared-ref-key note in the file
  // header. Don't flip it without first moving settlementId from this ref into
  // the key itself.
  const disbursementsSettlementId = ref<string | null>(null);
  const disbursementsQuery = useQuery({
    key: () => ['settlement', 'disbursements', disbursementsSettlementId.value],
    query: ({ signal }) => getDisbursementsApi(disbursementsSettlementId.value as string, signal),
    enabled: false,
    staleTime: 0,
  });
  /** Bumped on every loadDisbursements() call so a superseded call's `finally` can't
   *  clear `loading`/`error` out from under a newer call still in flight — mirrors
   *  the `expenses.requestToken` guard below. Colada's `fetch()` aborts the older
   *  call's network request for the same key, but that older call's own promise
   *  still settles (rejects) and runs its own `finally`, so this is still needed. */
  let disbursementsLoadToken = 0;

  /**
   * Load the disbursement rows for the table. Safe to call repeatedly — a new
   * call aborts any in-flight read so a stale response can't win (Colada's
   * `fetch()` aborts the previous in-flight fetch for this query key before
   * starting a new one, and only the latest call's success updates the cache
   * entry).
   *
   * By default this also clears the transient per-row submit errors, since a
   * fresh load (mount, matter change, manual retry) should never show a stale
   * inline error from a previous context. The post-op refresh passes
   * `preserveRowErrors: true` so a just-recorded failure survives the reload.
   */
  async function loadDisbursements(options: { preserveRowErrors?: boolean } = {}): Promise<void> {
    if (!settlementId.value) return;
    if (!options.preserveRowErrors) {
      disbursements.rowErrors = {};
      disbursements.submittingKeys = [];
    }
    const token = ++disbursementsLoadToken;
    disbursementsSettlementId.value = settlementId.value;
    disbursements.loading = true;
    disbursements.error = null;
    try {
      const state = await disbursementsQuery.refetch(true);
      if (token !== disbursementsLoadToken) return; // superseded by a newer load
      disbursements.data = state.data ?? null;
    } catch (err: any) {
      if (token !== disbursementsLoadToken) return; // superseded — resolve quietly
      // Cancellations are expected when a load is superseded/unmounted — ignore.
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      disbursements.error = err?.response?.data?.message || 'Unable to load disbursements';
    } finally {
      // Only the latest load owns the loading flag.
      if (token === disbursementsLoadToken) {
        disbursements.loading = false;
      }
    }
  }

  /** Cancel any in-flight disbursements read (e.g. on unmount / navigate away). */
  function cancelDisbursementsLoad(): void {
    queryCache.cancelQueries({ key: ['settlement', 'disbursements', disbursementsSettlementId.value] });
  }

  const submitDisbursementsMutation = useMutation({
    mutation: (vars: { settlementId: string; rows: DisbursementRowRef[] }) =>
      submitDisbursementsApi(vars.settlementId, vars.rows),
  });

  const undoDisbursementsMutation = useMutation({
    mutation: (vars: { settlementId: string; rows: DisbursementRowRef[] }) =>
      undoDisbursementsApi(vars.settlementId, vars.rows),
  });

  /**
   * Submit the given rows to QuickBooks in one backend call (selective bulk;
   * a single row is a one-item list). The firm-invoice row runs finalize->pay
   * as one saga server-side. Returns per-row results; failures are recorded as
   * inline row errors and the table is refreshed from the server afterwards.
   */
  async function submitDisbursements(refs: DisbursementRowRef[]): Promise<DisbursementRowResult[]> {
    return _runDisbursementOp(refs, submitDisbursementsMutation, 'submit');
  }

  /** Undo (reverse the QBO entry for) the given rows — mirror of submit. */
  async function undoDisbursements(refs: DisbursementRowRef[]): Promise<DisbursementRowResult[]> {
    return _runDisbursementOp(refs, undoDisbursementsMutation, 'undo');
  }

  const recordManualDisbursementMutation = useMutation({
    mutation: (vars: { settlementId: string }) => recordManualDisbursementApi(vars.settlementId),
  });

  const sendClientDisbursementMutation = useMutation({
    mutation: (vars: { settlementId: string; payload: SendClientDisbursementPayload }) =>
      sendClientDisbursementApi(vars.settlementId, vars.payload),
  });

  /**
   * Send the client's disbursement request (sc-46627). Same busy/error/reload discipline as
   * recordManualClientDisbursement — both write a QBO check and move the client-net state.
   *
   * Resolves false when a send was already in flight and this call was suppressed, so a caller
   * can tell "nothing was posted" from "posted successfully" — closing the compose modal on a
   * suppressed duplicate would look exactly like a send that went out.
   */
  async function sendClientDisbursement(
    payload: SendClientDisbursementPayload,
  ): Promise<boolean> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    if (disbursements.submittingKeys.includes(CLIENT_ROW_KEY)) return false;

    disbursements.submittingKeys = [...disbursements.submittingKeys, CLIENT_ROW_KEY];
    const clearedErrors = { ...disbursements.rowErrors };
    delete clearedErrors[CLIENT_ROW_KEY];
    disbursements.rowErrors = clearedErrors;

    try {
      await sendClientDisbursementMutation.mutateAsync({
        settlementId: settlementId.value,
        payload,
      });
      await _reloadAfterDisbursementOp();
      return true;
    } catch (err: any) {
      // The backend distinguishes the trust shortfall, the Payabli connection gate, the
      // payout-eligibility block and the VR-005 conflict; none of that should be re-authored here.
      const msg =
        err?.response?.data?.message
        || 'Unable to send the deposit request. Please try again.';
      disbursements.rowErrors = { ...disbursements.rowErrors, [CLIENT_ROW_KEY]: msg };
      useSnackbarHandler().handleError(err);
      throw err;
    } finally {
      disbursements.submittingKeys = disbursements.submittingKeys.filter(
        (k) => k !== CLIENT_ROW_KEY,
      );
    }
  }

  /**
   * Record the client's disbursement as paid outside Payabli (sc-46626). Deliberately not routed
   * through _runDisbursementOp: that one speaks the per-row submit/undo result protocol, and this
   * endpoint returns a single record-or-throw. The busy/error/reload discipline is the same.
   */
  async function recordManualClientDisbursement(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    if (disbursements.submittingKeys.includes(CLIENT_ROW_KEY)) return;

    disbursements.submittingKeys = [...disbursements.submittingKeys, CLIENT_ROW_KEY];
    const clearedErrors = { ...disbursements.rowErrors };
    delete clearedErrors[CLIENT_ROW_KEY];
    disbursements.rowErrors = clearedErrors;

    try {
      await recordManualDisbursementMutation.mutateAsync({ settlementId: settlementId.value });
      // Same reload set as submit/undo: this writes a QBO check and moves the settlement's
      // client-net state, which settlement-level flags and the statement PDF both depend on.
      await _reloadAfterDisbursementOp();
    } catch (err: any) {
      // Prefer the backend's copy — it distinguishes the VR-005 conflict, the missing-QuickBooks
      // case and the client-accounting refusal, none of which the FE should re-author.
      const msg =
        err?.response?.data?.message
        || 'Unable to record the manual disbursement. Please try again.';
      disbursements.rowErrors = { ...disbursements.rowErrors, [CLIENT_ROW_KEY]: msg };
      useSnackbarHandler().handleError(msg);
      throw err;
    } finally {
      disbursements.submittingKeys = disbursements.submittingKeys.filter(
        (k) => k !== CLIENT_ROW_KEY,
      );
    }
  }

  const cancelClientDisbursementMutation = useMutation({
    mutation: (vars: { settlementId: string }) => cancelClientDisbursementApi(vars.settlementId),
  });

  /**
   * Cancel the client's payment link (sc-46628). Same busy/error/reload discipline as
   * recordManualClientDisbursement, except that a refusal reloads too: the backend refuses with a
   * 409 once the payee has submitted, and the row has to catch up so the stale Cancel disappears.
   */
  async function cancelClientDisbursement(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    if (disbursements.submittingKeys.includes(CLIENT_ROW_KEY)) return;

    disbursements.submittingKeys = [...disbursements.submittingKeys, CLIENT_ROW_KEY];
    const clearedErrors = { ...disbursements.rowErrors };
    delete clearedErrors[CLIENT_ROW_KEY];
    disbursements.rowErrors = clearedErrors;

    try {
      const result = await cancelClientDisbursementMutation.mutateAsync({
        settlementId: settlementId.value,
      });
      await _reloadAfterDisbursementOp();
      if (result?.qboReversalStatus === 'Pending') {
        const payee = disbursements.data?.clientRow?.recipientName || 'the client';
        useSnackbarHandler().handleWarning(
          `Payment link canceled for ${payee}, but QuickBooks sync failed. `
          + 'The trust debit is still recorded. Use Retry QuickBooks Sync on the row.',
        );
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message
        || 'Unable to cancel the payment link. Please try again.';
      disbursements.rowErrors = { ...disbursements.rowErrors, [CLIENT_ROW_KEY]: msg };
      useSnackbarHandler().handleError(msg);
      await loadDisbursements({ preserveRowErrors: true });
      throw err;
    } finally {
      disbursements.submittingKeys = disbursements.submittingKeys.filter(
        (k) => k !== CLIENT_ROW_KEY,
      );
    }
  }

  const disbursementEmailTarget = ref<{ settlementId: string; disbursementId: string } | null>(null);
  const disbursementEmailQuery = useQuery({
    key: () => [
      'settlement',
      'disbursement-email',
      disbursementEmailTarget.value?.settlementId,
      disbursementEmailTarget.value?.disbursementId,
    ],
    query: () => {
      const target = disbursementEmailTarget.value;
      if (!target) throw new Error('A disbursement is required');
      return getDisbursementEmailApi(target.settlementId, target.disbursementId);
    },
    enabled: false,
    staleTime: 0,
  });

  /** The composition last sent for the client's disbursement — the resend modal's pre-fill. */
  async function loadClientDisbursementEmail(): Promise<DisbursementEmailComposition | null> {
    const disbursementId = disbursements.data?.clientRow?.payabli?.disbursementId;
    if (!settlementId.value || !disbursementId) return null;
    disbursementEmailTarget.value = { settlementId: settlementId.value, disbursementId };
    const state = await disbursementEmailQuery.refetch(true);
    return state.data ?? null;
  }

  const resendClientDisbursementEmailMutation = useMutation({
    mutation: (vars: {
      settlementId: string;
      disbursementId: string;
      payload: SendClientDisbursementPayload;
    }) => resendDisbursementEmailApi(vars.settlementId, vars.disbursementId, vars.payload),
  });

  /**
   * Resend the client's disbursement email (sc-47502). Leaves the payment link and the QuickBooks
   * check alone. Same busy/error/reload discipline as sendClientDisbursement, including resolving
   * false on a suppressed duplicate. An email that still doesn't queue rejects, so the modal stays
   * open with the reason instead of closing on a resend that didn't go out.
   */
  async function resendClientDisbursementEmail(
    payload: SendClientDisbursementPayload,
  ): Promise<boolean> {
    const disbursementId = disbursements.data?.clientRow?.payabli?.disbursementId;
    if (!settlementId.value || !disbursementId) throw new Error('A sent disbursement is required');
    if (disbursements.submittingKeys.includes(CLIENT_ROW_KEY)) return false;

    disbursements.submittingKeys = [...disbursements.submittingKeys, CLIENT_ROW_KEY];
    const clearedErrors = { ...disbursements.rowErrors };
    delete clearedErrors[CLIENT_ROW_KEY];
    disbursements.rowErrors = clearedErrors;

    try {
      const result = await resendClientDisbursementEmailMutation.mutateAsync({
        settlementId: settlementId.value,
        disbursementId,
        payload,
      });
      if (!result?.emailQueued) {
        throw new Error(
          "The email still couldn't be sent. The payment link and QuickBooks check are unaffected — "
          + 'try again in a few minutes.',
        );
      }
      await loadDisbursements();
      return true;
    } catch (err: any) {
      const msg =
        err?.response?.data?.message
        || err?.message
        || 'Unable to resend the email. Please try again.';
      disbursements.rowErrors = { ...disbursements.rowErrors, [CLIENT_ROW_KEY]: msg };
      useSnackbarHandler().handleError(msg);
      // A 409 means the row moved on (canceled, or the payee acted) — the stale Resend has to go.
      await loadDisbursements({ preserveRowErrors: true });
      throw err;
    } finally {
      disbursements.submittingKeys = disbursements.submittingKeys.filter(
        (k) => k !== CLIENT_ROW_KEY,
      );
    }
  }

  /**
   * Internal (leading underscore): the shared submit/undo runner behind
   * submitDisbursements / undoDisbursements — not meant to be called directly by
   * components. Marks rows in-flight, records per-row failures as inline errors,
   * surfaces a summary snackbar only for bulk ops, and always re-reads the rows
   * from the server so statuses reflect backend truth. Ignores a call whose rows
   * are already in-flight (guards double-clicks / concurrent 409s).
   */
  async function _runDisbursementOp(
    refs: DisbursementRowRef[],
    mutation: typeof submitDisbursementsMutation | typeof undoDisbursementsMutation,
    kind: 'submit' | 'undo',
  ): Promise<DisbursementRowResult[]> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    if (!refs.length) return [];
    const keys = refs.map(rowKeyForRef);
    // Guard: ignore if any of these rows is already being processed.
    if (keys.some((k) => disbursements.submittingKeys.includes(k))) {
      return [];
    }

    disbursements.submittingKeys = [...disbursements.submittingKeys, ...keys];
    // Clear any prior inline errors for the rows we're about to retry.
    const clearedErrors = { ...disbursements.rowErrors };
    keys.forEach((k) => delete clearedErrors[k]);
    disbursements.rowErrors = clearedErrors;
    disbursements.bulkBusy = refs.length > 1;

    const verb = kind === 'submit' ? 'submitted' : 'reversed';
    try {
      const results = await mutation.mutateAsync({ settlementId: settlementId.value, rows: refs });

      const nextErrors = { ...disbursements.rowErrors };
      results.forEach((r) => {
        if (!r.success) {
          nextErrors[r.key] = r.errorMessage || `This disbursement could not be ${verb}. Please try again.`;
        }
      });
      disbursements.rowErrors = nextErrors;

      const failures = results.filter((r) => !r.success);
      // Single-row failures show inline only; bulk ops get a summary snackbar.
      if (failures.length && refs.length > 1) {
        useSnackbarHandler().handleError(
          `${failures.length} of ${results.length} disbursements could not be ${verb}.`,
        );
      }
      // Partial failures arrive as results, not throws, so this still runs for them.
      await _reloadAfterDisbursementOp();
      return results;
    } catch (err: any) {
      // Prefer the backend's message (e.g. the 409 lock-conflict copy) — the FE
      // doesn't author product copy here — with a minimal generic fallback.
      const msg = err?.response?.data?.message || `Unable to ${kind} disbursements. Please try again.`;
      const failed = { ...disbursements.rowErrors };
      keys.forEach((k) => {
        failed[k] = msg;
      });
      disbursements.rowErrors = failed;
      useSnackbarHandler().handleError(msg);
      // A throw can still leave partial server-side state (e.g. a timeout after the write landed),
      // so refresh — but guarded, the way finalizeInvoice does: a failing refresh must not replace
      // the error the user needs to see, nor block the cleanup below.
      try {
        await _reloadAfterDisbursementOp();
      } catch {
        /* keep the prior rows and the error recorded above */
      }
      throw err;
    } finally {
      // e.g. finalizing the firm invoice flips `invoiceFinalized`, which gates
      // the Trust Statement download link — without this it wouldn't appear
      // until the tab was re-entered.
      //
      disbursements.submittingKeys = disbursements.submittingKeys.filter((k) => !keys.includes(k));
      disbursements.bulkBusy = false;
    }
  }

  /**
   * Re-read the rows AND the settlement so both the table (per-row status) and settlement-level
   * derived flags refresh. The latter matters because e.g. finalizing the firm invoice flips
   * `invoiceFinalized`, which gates the Trust Statement download link — without this it wouldn't
   * appear until the tab was re-entered.
   *
   * Runs on the success path only, and before the in-flight keys are cleared, so the row keeps its
   * in-progress overlay rather than flashing back to "Submit" for the reload's round trip. A thrown
   * op has written nothing to re-read, and awaiting this in a `finally` is what used to strand the
   * spinner when the reload itself never settled.
   */
  async function _reloadAfterDisbursementOp(): Promise<void> {
    await Promise.all([
      // Preserve the per-row errors we just recorded from this op's results.
      loadDisbursements({ preserveRowErrors: true }),
      _refreshSettlementData(),
    ]);
    // The backend regenerates the statement PDF after a successful submit/undo, but
    // fetchStatementBlobUrl() reuses a cached blob URL once fetched — drop it and
    // re-fetch now (revoke alone would hide the Download button), mirroring
    // finalizeInvoice/undoInvoice's identical statement-cache-busting step.
    revokeStatementBlobUrl();
    await fetchStatementBlobUrl();
  }

  /** Re-fetch the settlement record so derived getters (invoiceFinalized,
   *  canReset, trustPaid, qboPushed, netToClient…) reflect a disbursement op.
   *  Non-fatal: the op already succeeded, so a refresh failure is swallowed. */
  async function _refreshSettlementData(): Promise<void> {
    if (!settlementId.value) return;
    try {
      data.value = await getSettlementById(settlementId.value);
    } catch {
      /* leave existing data in place */
    }
  }

  /** Reset store state (e.g. when navigating away). */
  function reset(): void {
    loading.value = true;
    saving.value = false;
    data.value = null;
    settlementId.value = null;
    error.value = null;
    Object.assign(deposits, createInitialDeposits());
    Object.assign(expenses, createInitialExpenses());
    coCounselExpenses.value = [];
    Object.assign(liens, createInitialLiens());
    fromTableLiens.value = null;
    Object.assign(attorneyFee, createInitialAttorneyFee());
    Object.assign(statement, createInitialStatement());
    Object.assign(payments, createInitialPayments());
    Object.assign(postSettlement, createInitialPostSettlement());
    Object.assign(disbursements, createInitialDisbursements());
    settlementDate.value = null;
    companyExpenseInterestRate.value = null;
    Object.assign(narrative, createInitialNarrative());
  }

  // ── Step 2: Expenses ───────────────────────────────

  // enabled: false is load-bearing here — see the shared-ref-key note in the file
  // header. Don't flip it without first moving matterId/orderby/order from this
  // ref into the key itself.
  const expensesQueryParams = ref<{ matterId: string; orderby: string; order: 'asc' | 'desc' } | null>(
    null,
  );
  const expensesQuery = useQuery({
    key: () => [
      'settlement',
      'expenses',
      expensesQueryParams.value?.matterId,
      expensesQueryParams.value?.orderby,
      expensesQueryParams.value?.order,
    ],
    query: () => {
      const params = expensesQueryParams.value!;
      return getMatterExpenses(params.matterId, { orderby: params.orderby, order: params.order });
    },
    enabled: false,
    staleTime: 0,
  });

  // ClientPays only: preview of case costs already billed on a prior invoice, outside this
  // step's unbilled batch (sc-46645). Same enabled:false/refetch pattern as expensesQuery above.
  const billedExpensesMatterId = ref<string | null>(null);
  const billedExpensesQuery = useQuery({
    key: () => ['settlement', 'billed-expenses', billedExpensesMatterId.value],
    query: () => getMatterBilledExpenses(billedExpensesMatterId.value as string),
    enabled: false,
    staleTime: 0,
  });

  /**
   * Load expenses for the matter via GET /v1/expenses?matterId=...
   * Stores the list and total for display.
   *
   * `sort` is optional — when omitted, reuses whatever orderby/order is already tracked in
   * state (defaulting to date/asc), so reloads unrelated to sorting (post-edit, matter change)
   * preserve the user's currently active sort instead of resetting it.
   *
   * Guards against out-of-order responses with a monotonic request token: if a newer
   * loadExpenses() call starts before this one resolves, this call's result is discarded.
   * (Colada's own per-entry "only the latest pending call wins" guard protects the shared
   * cache entry, but this action still needs its own token to decide whether ITS caller
   * should apply the result to `expenses.*` — the two guards serve different owners.)
   */
  async function loadExpenses(
    matterId: string,
    sort?: { orderby?: string; order?: 'asc' | 'desc' },
  ): Promise<void> {
    const orderby = sort?.orderby ?? expenses.orderby;
    const order = sort?.order ?? expenses.order;
    const requestToken = (expenses.requestToken += 1);
    expenses.loading = true;
    expenses.error = null;
    expensesQueryParams.value = { matterId, orderby, order };
    try {
      const state = await expensesQuery.refetch(true);
      const response = state.data!;
      if (requestToken !== expenses.requestToken) return; // superseded by a newer request
      expenses.orderby = orderby;
      expenses.order = order;
      const interestExpenseId = data.value?.interestExpenseId;
      expenses.items = response.expenses.filter(
        (e) => !e.isExpenseInterest && e.id !== interestExpenseId,
      );
      // Sum in integer cents to avoid float drift — this total feeds hasNegativeFeeBase.
      expenses.totalExpenses =
        expenses.items.reduce((cents, e) => cents + Math.round(e.amount * 100), 0) / 100;

      // ClientPays only: preview Total Case Costs (unbilled + previously billed) so this step
      // shows the same figure ConfirmExpensesAsync will save — backend independently re-derives
      // this at confirm time, this is display-only (sc-46645). Isolated in its own try/catch:
      // this is a secondary preview fetch, and letting it fail the whole loadExpenses() would
      // block Step 2 (interest state, as-of date, etc.) over a fetch confirmStep2 doesn't
      // actually depend on. Falls back to 0/[] and lets the next reload retry.
      if (data.value?.expenseArrangement === 'Client') {
        try {
          billedExpensesMatterId.value = matterId;
          const billedState = await billedExpensesQuery.refetch(true);
          const billedResponse = billedState.data!;
          if (requestToken !== expenses.requestToken) return; // superseded by a newer request
          expenses.billedItems = billedResponse.expenses.filter(
            (e) => !e.isExpenseInterest && e.id !== interestExpenseId,
          );
          expenses.billedExpensesTotal =
            expenses.billedItems.reduce((cents, e) => cents + Math.round(e.amount * 100), 0) / 100;
        } catch {
          expenses.billedItems = [];
          expenses.billedExpensesTotal = 0;
        }
      } else {
        expenses.billedItems = [];
        expenses.billedExpensesTotal = 0;
      }

      // Restore persisted as-of date, or default to today (FRD §V2-3.2).
      if (!expenses.expenseFromDate) {
        expenses.expenseFromDate = data.value?.expenseFromDate ?? yyyymmdddash(new Date());
      }
      // Sync confirmed state with server — true when the settlement already has an interest expense.
      expenses.interestConfirmed = !!data.value?.interestExpenseId;
      if (expenses.interestConfirmed) {
        // Snapshot the CONFIRMED state from the server (frozen as-of date + rate), NOT the current UI
        // as-of date. The store isn't reset on unmount and the step uses onActivated, so reading the
        // live UI date here would mark a locally-changed-but-unconfirmed date as "confirmed" after
        // navigating away and back, wrongly disabling the re-confirm gate (interestNeedsReconfirm).
        const confirmedAsOf = data.value?.expenseFromDate ?? null;
        const confirmedRate =
          data.value?.expenseInterestRateUsed ?? data.value?.companyExpenseInterestRate ?? null;
        expenses.confirmedExpenseFromDate = confirmedAsOf;
        expenses.confirmedTotalInterest =
          confirmedAsOf && confirmedRate
            ? expenses.items.reduce(
                (cents, e) =>
                  e.date
                    ? cents +
                      Math.round(
                        computeExpenseInterest(e.amount, confirmedRate, daysBetween(e.date, confirmedAsOf)) * 100,
                      )
                    : cents,
                0,
              ) / 100
            : 0;
      }
    } catch (err: any) {
      if (requestToken !== expenses.requestToken) return; // superseded — resolve quietly
      expenses.error = err;
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to load expenses');
      throw err;
    } finally {
      if (requestToken === expenses.requestToken) {
        expenses.loading = false;
      }
    }
  }

  // Shared by removeInterest and confirmInterest — both PATCH the same expense-
  // interest endpoint with just a different payload (zeroed vs computed amount).
  const expenseInterestMutation = useMutation({
    mutation: (vars: {
      settlementId: string;
      payload: { expenseFromDate: string; totalInterestAmount: number; interestRateUsed: number | null };
    }) => confirmExpenseInterest(vars.settlementId, vars.payload),
  });

  /** Remove a previously confirmed interest expense (sends totalInterestAmount: 0). */
  async function removeInterest(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    if (!data.value?.interestExpenseId) return;
    saving.value = true;
    const fromDate =
      expenses.expenseFromDate ??
      (data.value.expenseFromDate ? String(data.value.expenseFromDate) : yyyymmdddash(new Date()));
    try {
      const response = await expenseInterestMutation.mutateAsync({
        settlementId: settlementId.value,
        payload: {
          expenseFromDate: fromDate,
          totalInterestAmount: 0,
          interestRateUsed: data.value?.companyExpenseInterestRate ?? null,
        },
      });
      data.value = response;
      expenses.interestConfirmed = false;
      expenses.confirmedExpenseFromDate = null;
      expenses.confirmedTotalInterest = 0;
      expenses.items = expenses.items.filter(
        (e) => !e.isExpenseInterest && e.id !== data.value?.interestExpenseId,
      );
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to remove interest expense',
      );
      throw err;
    } finally {
      saving.value = false;
    }
  }

  /** Confirm the interest expense before advancing the expense step. */
  async function confirmInterest(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    if (!expenses.expenseFromDate) throw new Error('Interest as-of date is required');
    saving.value = true;
    try {
      const response = await expenseInterestMutation.mutateAsync({
        settlementId: settlementId.value,
        payload: {
          expenseFromDate: expenses.expenseFromDate,
          totalInterestAmount: totalInterest.value,
          interestRateUsed: data.value?.companyExpenseInterestRate ?? null,
        },
      });
      data.value = response;
      expenses.interestConfirmed = true;
      expenses.confirmedExpenseFromDate = expenses.expenseFromDate;
      expenses.confirmedTotalInterest = totalInterest.value;
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to confirm interest expense',
      );
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const coCounselExpensesSettlementId = ref<string | null>(null);
  const coCounselExpensesQuery = useQuery({
    key: () => ['settlement', 'co-counsel-expenses', coCounselExpensesSettlementId.value],
    query: () => getCoCounselExpensesApi(coCounselExpensesSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  /** Load Step 2's co-counsel/referring-attorney expense rows. Always calls the
   *  endpoint the same way — it already joins live recipients with any confirmed
   *  amount (defaulting to 0), so there's no need to branch on Step 3 state. */
  async function loadCoCounselExpenses(): Promise<void> {
    if (!settlementId.value) return;
    coCounselExpensesSettlementId.value = settlementId.value;
    try {
      const state = await coCounselExpensesQuery.refetch(true);
      const response = state.data!;
      coCounselExpenses.value = response.coCounselExpenses.map(mapCoCounselExpense);
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to load co-counsel expenses',
      );
    }
  }

  /** Local edit of a co-counsel row's amount — committed to the backend on confirmStep2. */
  function updateCoCounselExpenseAmount(vendorId: string, amount: number): void {
    const idx = coCounselExpenses.value.findIndex((e) => e.vendorId === vendorId);
    if (idx === -1) return;
    coCounselExpenses.value[idx] = { ...coCounselExpenses.value[idx], amount };
  }

  const confirmStep2Mutation = useMutation({
    mutation: (vars: { settlementId: string; payload: ConfirmExpensesRequest }) =>
      confirmExpenses(vars.settlementId, vars.payload),
  });

  async function confirmStep2(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    saving.value = true;
    // Capture before data is overwritten by the response
    const prevInterestExpenseId = data.value?.interestExpenseId;
    try {
      // Exclude the interest soft-cost from the payload — it is managed separately.
      // Filter by both flag (forward-compat) and ID (the real discriminator since the
      // backend never sends isExpenseInterest: true in the expense list).
      const regularExpenses = expenses.items.filter(
        (e) => !e.isExpenseInterest && e.id !== prevInterestExpenseId,
      );
      // Sum in integer cents so binary float error (e.g. 0.1 + 0.2) isn't persisted to the backend.
      const totalExpenses =
        regularExpenses.reduce((cents, e) => cents + Math.round(e.amount * 100), 0) / 100;
      const coCounselRows = coCounselExpenses.value.map((e) => ({
        recipientId: e.id,
        // Round to cents (same reasoning as totalExpenses above) so binary float
        // error isn't persisted to the backend.
        amount: Math.round(e.amount * 100) / 100,
        ...(e.role !== 'coCounsel' ? { role: e.role } : {}),
      }));
      const response = await confirmStep2Mutation.mutateAsync({
        settlementId: settlementId.value,
        payload: {
          totalExpenses,
          expenses: regularExpenses.map((e) => ({
            expenseId: e.id,
            amount: e.amount,
            description: e.description,
          })),
          totalInterestAmount: totalInterest.value,
          ...(coCounselRows.length > 0 ? { coCounselExpenses: coCounselRows } : {}),
        },
      });
      data.value = response;
      expenses.isConfirmed = true;
      if (!response.interestExpenseId) {
        expenses.items = expenses.items.filter(
          (e) => !e.isExpenseInterest && e.id !== prevInterestExpenseId,
        );
        expenses.interestConfirmed = false;
        expenses.confirmedExpenseFromDate = null;
        expenses.confirmedTotalInterest = 0;
      }
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to confirm expenses');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  // ── Step 4: Liens ──────────────────────────────────

  const liensFromTableSettlementId = ref<string | null>(null);
  const liensFromTableQuery = useQuery({
    key: () => ['settlement', 'liens', 'from-table', liensFromTableSettlementId.value],
    query: () => getLiensFromTable(liensFromTableSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  // MatterSettlement.vue also fires this fire-and-forget on mount/resume — see
  // attorneyFeeLoadToken above for why an overlapping call from
  // SettlementLienStepFF.vue's own onMounted needs a token guard here too.
  let liensFromTableLoadToken = 0;

  /**
   * Load liens from the Liens tab table (flipper ON path).
   * Calls GET /settlements/{settlementId}/liens/from-table.
   *
   * Always force-fresh: SettlementLienStepFF.vue calls this from four separate
   * places (mount, retry, and again right after confirmLiensFF() succeeds), so
   * there's no "frozen snapshot vs live" split here worth caching.
   */
  async function loadLiensFromTable(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    const token = ++liensFromTableLoadToken;
    liens.loading = true;
    liens.error = null;
    liensFromTableSettlementId.value = settlementId.value;
    try {
      const state = await liensFromTableQuery.refetch(true);
      if (token !== liensFromTableLoadToken) return; // superseded by a newer request
      const response = state.data!;
      fromTableLiens.value = response;
      liens.totalLiens = response.totalLiens;
    } catch (err: any) {
      if (token !== liensFromTableLoadToken) return; // superseded — resolve quietly
      liens.error = err;
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to load liens');
    } finally {
      if (token === liensFromTableLoadToken) {
        liens.loading = false;
      }
    }
  }

  const confirmLiensFFMutation = useMutation({
    mutation: (vars: { settlementId: string }) =>
      confirmLiensApi(vars.settlementId, { selectedLiens: [] }),
  });

  /**
   * Confirm Step 4 (flipper ON): no selectedLiens payload.
   * Calls POST /settlements/{settlementId}/liens/confirm with empty array.
   */
  async function confirmLiensFF(): Promise<void> {
    if (!settlementId.value) throw new Error('Settlement ID is required');
    saving.value = true;
    try {
      const response = await confirmLiensFFMutation.mutateAsync({ settlementId: settlementId.value });
      data.value = response;
    } catch (err: any) {
      useSnackbarHandler().handleError(err?.response?.data?.message || 'Unable to confirm liens');
      throw err;
    } finally {
      saving.value = false;
    }
  }

  const narrativeSettlementId = ref<string | null>(null);
  const narrativeQuery = useQuery({
    key: () => ['settlement', 'narrative', narrativeSettlementId.value],
    query: () => getNarrativeApi(narrativeSettlementId.value as string),
    enabled: false,
    staleTime: 0,
  });

  async function fetchNarrative(settlementIdParam: string): Promise<void> {
    narrative.loading = true;
    narrativeSettlementId.value = settlementIdParam;
    try {
      const state = await narrativeQuery.refetch(true);
      const dto = state.data!;
      narrative.enableOpeningParagraph = dto.enableOpeningParagraph;
      narrative.openingParagraphHtml = dto.openingParagraphHtml;
      narrative.enableClosingParagraph = dto.enableClosingParagraph;
      narrative.closingParagraphHtml = dto.closingParagraphHtml;
      narrative.jurisdictionNote = dto.jurisdictionNote;
      narrative.isCustomized = dto.isCustomized;
      narrative.editorLock = dto.editorLock;
      narrative.updatedAt = dto.modifiedDate;
      narrative.isStale = false;
    } finally {
      narrative.loading = false;
    }
  }

  const saveNarrativeMutation = useMutation({
    mutation: (vars: { settlementId: string; content: NarrativeContentInput }) =>
      saveNarrativeApi(vars.settlementId, vars.content),
  });

  async function saveNarrative(settlementIdParam: string, content: NarrativeContentInput): Promise<void> {
    narrative.saving = true;
    try {
      const dto = await saveNarrativeMutation.mutateAsync({ settlementId: settlementIdParam, content });
      narrative.enableOpeningParagraph = dto.enableOpeningParagraph;
      narrative.openingParagraphHtml = dto.openingParagraphHtml;
      narrative.enableClosingParagraph = dto.enableClosingParagraph;
      narrative.closingParagraphHtml = dto.closingParagraphHtml;
      narrative.jurisdictionNote = dto.jurisdictionNote;
      narrative.isCustomized = dto.isCustomized;
      narrative.updatedAt = dto.modifiedDate;
      narrative.isStale = false;
    } finally {
      narrative.saving = false;
    }
  }

  const restoreNarrativeFromTemplateMutation = useMutation({
    mutation: (vars: { settlementId: string }) => restoreNarrativeFromTemplateApi(vars.settlementId),
  });

  async function restoreNarrativeFromTemplate(settlementIdParam: string): Promise<void> {
    try {
      const dto = await restoreNarrativeFromTemplateMutation.mutateAsync({ settlementId: settlementIdParam });
      narrative.enableOpeningParagraph = dto.enableOpeningParagraph;
      narrative.openingParagraphHtml = dto.openingParagraphHtml;
      narrative.enableClosingParagraph = dto.enableClosingParagraph;
      narrative.closingParagraphHtml = dto.closingParagraphHtml;
      narrative.jurisdictionNote = dto.jurisdictionNote;
      narrative.isCustomized = dto.isCustomized;
      narrative.updatedAt = dto.modifiedDate;
      narrative.isStale = true;
    } catch (err: any) {
      useSnackbarHandler().handleError(
        err?.response?.data?.message || 'Unable to restore from firm template',
      );
      throw err;
    }
  }

  async function acquireEditorLock(settlementIdParam: string): Promise<void> {
    try {
      const lock = await acquireNarrativeLockApi(settlementIdParam);
      narrative.editorLock = lock;
    } catch (err: any) {
      if (err?.response?.status !== 409) throw err;
      // 409: another user holds the lock — editorLock already set from fetchNarrative
    }
  }

  async function releaseEditorLock(settlementIdParam: string): Promise<void> {
    await releaseNarrativeLockApi(settlementIdParam);
    narrative.editorLock = null;
  }

  function markNarrativeStale(): void {
    narrative.isStale = true;
  }

  function markNarrativeFresh(): void {
    narrative.isStale = false;
  }

  return {
    // state
    loading,
    saving,
    data,
    settlementId,
    error,
    deposits,
    expenses,
    coCounselExpenses,
    liens,
    fromTableLiens,
    attorneyFee,
    statement,
    payments,
    postSettlement,
    disbursements,
    settlementDate,
    companyExpenseInterestRate,
    narrative,
    // getters
    completedSteps,
    currentStep,
    hasCompletedSteps,
    recoveryAmount,
    canConfirmStep1,
    allDepositsSelected,
    displayPayments,
    clientPaidTotal,
    canConfirmStep3,
    feeMatchStatus,
    canGenerateStatement,
    isGenerated,
    isLocked,
    canReset,
    resetBlockedReason,
    settingsLocked,
    disbursementBlockedReason,
    undoDisbursementBlockedReason,
    statementDownloadUrl,
    previewDownloadUrl,
    settings,
    lienCount,
    hasNegativeFeeBase,
    totalInterest,
    invoiceFinalized,
    canFinalizeInvoice,
    canUndoInvoice,
    invoiceBlockedReason,
    trustPaid,
    qboPushed,
    canPushClientDisbursement,
    canUndoClientDisbursement,
    invoiceAmount,
    invoiceBalance,
    trustBalance,
    canPayFromTrust,
    canUndoTrustPayment,
    trustPaymentBlockedReason,
    undoTrustPaymentBlockedReason,
    netToClient,
    trustInsufficient,
    // actions
    load,
    loadDeposits,
    toggleDeposit,
    toggleAllDeposits,
    updateSettings,
    resetAndUpdateSettings,
    lockStep,
    generate,
    retryPolling,
    fetchStatementBlobUrl,
    downloadTrustStatementBlob,
    resetStatementAndExpenses,
    revokeStatementBlobUrl,
    unlock,
    resetSettlementById,
    resetSettlementsForClient,
    generatePreview,
    fetchPreviewBlobUrl,
    revokePreviewBlobUrl,
    retryPreview,
    createSettlement,
    confirmDeposit,
    loadAttorneyFee,
    loadMatterFeeRecipients,
    setFeeRecipients,
    selectFee,
    recordFee,
    updateFee,
    confirmAttorneyFee,
    confirmAttorneyFeeRedesign,
    loadPayments,
    togglePayment,
    confirmClientPayments,
    loadTrustBalance,
    finalizeInvoice,
    undoInvoice,
    payFromTrust,
    undoTrustPayment,
    pushToQbo,
    undoQboDisbursement,
    loadDisbursements,
    cancelDisbursementsLoad,
    submitDisbursements,
    undoDisbursements,
    recordManualClientDisbursement,
    sendClientDisbursement,
    loadClientDisbursementEmail,
    resendClientDisbursementEmail,
    cancelClientDisbursement,
    reset,
    loadExpenses,
    removeInterest,
    confirmInterest,
    loadCoCounselExpenses,
    updateCoCounselExpenseAmount,
    confirmStep2,
    loadLiensFromTable,
    confirmLiensFF,
    fetchNarrative,
    saveNarrative,
    restoreNarrativeFromTemplate,
    acquireEditorLock,
    releaseEditorLock,
    markNarrativeStale,
    markNarrativeFresh,
  };
});
